import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Checkbox,
  keyframes,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Snackbar,
  Link,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Movie as MovieIcon,
  AllInclusive as AllInclusiveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  CloudUpload as CloudUploadIcon,
  SearchOff as SearchOffIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import axios from 'axios';

/** Manuel profil/story: proxy (Nginx/CF) genelde 60–100 sn; cron HTTP beklemediği için sorun çıkmaz */
const LONG_RUNNING_HTTP_MS = 15 * 60 * 1000;
/** Tek istek süresini kısaltmak için küçük paketler (sunucu tarafı throttling ile uyumlu) */
const MANUAL_HTTP_BATCH_SIZE = 2;

interface LinkTarget {
  url: string;
  matchType: 'contains' | 'exact';
}

type ContentTrackingMode = 'story' | 'feed' | 'both';

function getAutoCheckSectionCopy(mode: ContentTrackingMode | undefined): {
  title: string;
  description: string;
  undetectedHint: string;
  scopeLine: string;
} {
  const m = mode || 'story';
  if (m === 'feed') {
    return {
      title: 'Otomatik gönderi ve Reels kontrolü',
      description:
        'Sistem belirlediğiniz aralıklarla feed’deki gönderileri ve Reels’i kriterlere göre kontrol eder; eşleşen kayıtları raporlar.',
      undetectedHint:
        'Açıkken: görev kotası (hedef gönderi/Reels) tanımlıysa hedefe ulaşana kadar taranır; kota 0/0 ise ilk kriterle eşleşen gönderiden sonra bu kişi otomatik turlarda atlanır. API kullanımını azaltır.',
      scopeLine: 'Kapsam: feed gönderileri ve Reels (hikâye taraması yok).'
    };
  }
  if (m === 'both') {
    return {
      title: 'Otomatik hikâye ve gönderi kontrolü',
      description:
        'Sistem belirlediğiniz aralıklarla hem hikâyeleri hem feed gönderilerini/Reels’i periyodik olarak kontrol eder ve tespit edilen içerikleri kaydeder.',
      undetectedHint:
        'Açıkken: hedef hikâye/gönderi kotası olanlarda hedefler dolana kadar; kota 0/0 olanlarda ilk başarılı hikâye veya gönderi tespitinden sonra atlanır. API kullanımını azaltır.',
      scopeLine: 'Kapsam: hikâyeler ve feed gönderileri/Reels (sırayla taranır).'
    };
  }
  return {
    title: 'Otomatik hikâye kontrolü',
    description:
      'Sistem belirlediğiniz aralıklarla influencer hikâyelerini kontrol eder ve kriterlere uyan hikâyeleri kaydeder.',
    undetectedHint:
      'Açıkken: hedef hikâye kotası tanımlıysa hedefe ulaşana kadar taranır; kota 0/0 ise ilk kriterle eşleşen hikâyeden sonra otomatik turlarda atlanır. API kullanımını azaltır.',
    scopeLine: 'Kapsam: yalnızca hikâyeler.'
  };
}

interface Campaign {
  id: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  target_hashtags: string[];
  target_mentions: string[];
  target_links: (string | LinkTarget)[]; // Geriye uyumlu: string[] veya LinkTarget[]
  content_tracking_mode?: ContentTrackingMode;
  feed_post_limit?: number;
  target_caption_keywords?: string[];
  auto_check_enabled: boolean;
  auto_check_interval: number;
  auto_check_start_time?: string | null;
  auto_check_end_time?: string | null;
  check_only_undetected?: boolean;
  last_auto_check: string | null;
  influencers: Influencer[];
  recent_stories: Story[];
  recent_posts?: TrackedPost[];
  tracked_posts_total?: number;
  tracked_stories_total?: number;
  share_token?: string;
  share_enabled?: boolean;
  share_password?: string;
  share_slug?: string;
  server_time?: string;
}

interface Influencer {
  id: number;
  /** Gerçek influencers.id; profil silindiyse API satır kimliği için campaign_influencer_id kullanılır */
  campaign_influencer_id?: number;
  is_profile_detached?: boolean;
  influencer_id?: number | null;
  instagram_username: string;
  full_name: string;
  follower_count: number;
  profile_picture: string;
  instagram_url: string;
  is_verified: boolean;
  is_private: boolean;
  added_at: string;
  last_updated: string;
  /** null = henüz denenmedi, 1 = profil OK, 0 = ulaşılamadı */
  profile_reachable?: number | null;
  story_count?: number;
  detected_story_count?: number; // kampanya genelinde tespit edilen toplam hikaye
  detected_post_count?: number;
  last_detected_at?: string | null; // kampanya genelinde son tespit tarihi
  last_post_at?: string | null;
  task_quota_stories?: number;
  task_quota_posts?: number;
  task_status?: 'none' | 'in_progress' | 'completed';
  task_completed?: boolean;
}

interface Story {
  id: number;
  story_id: string;
  story_url: string;
  story_content: string;
  story_type: string;
  detected_keywords: string[];
  detected_at: string;
  story_timestamp: string;
  instagram_username: string;
  influencer_full_name?: string | null;
  media_url?: string | null;
  is_manual?: boolean | number;
}

interface TrackedPost {
  id: number;
  influencer_id: number;
  instagram_media_id: string;
  shortcode: string | null;
  product_type: string | null;
  is_reel: boolean;
  caption_text: string | null;
  thumbnail_url: string | null;
  permalink: string | null;
  detected_keywords: string[] | string;
  posted_at: string | null;
  detected_at: string;
  instagram_username: string;
  influencer_full_name?: string | null;
  local_media_path?: string | null;
  local_media_paths?: string[] | null;
  is_manual?: boolean;
}

type ManualMediaType = 'image' | 'video' | 'manual';

interface ManualEntry {
  id: number;
  campaign_id: number;
  influencer_id: number;
  influencer_username: string;
  influencer_full_name?: string;
  story_id?: string | null;
  story_url?: string | null;
  story_type: ManualMediaType;
  story_timestamp: string | null;
  media_url: string | null;
  detected_keywords: string[];
  detected_hashtags: string[];
  detected_mentions: string[];
  detected_links: string[];
  is_manual: boolean;
  added_by_username?: string | null;
  detected_at: string | null;
}

interface ManualEntryFormState {
  influencer_id: number | '';
  story_timestamp: string;
  story_type: ManualMediaType;
  media_url: string;
  file: File | null;
  selectedHashtags: string[];
  selectedMentions: string[];
  selectedLinks: string[];
}

interface ManualStoryPreview {
  story_id: string;
  story_url: string;
  owner_username: string | null;
  owner_username_match: boolean;
  suggested_influencer_id?: number | null;
  suggested_influencer_username?: string | null;
  existing_tracked_story_id?: number | null;
  existing_has_detection?: boolean;
  skipped?: boolean;
  message?: string;
  story_type?: 'image' | 'video' | null;
  media_url?: string | null;
  story_timestamp?: string | null;
  download_status?: 'downloaded' | 'failed' | 'skipped';
  download_error?: string | null;
}

interface ManualStoryBulkResultItem {
  url: string;
  status: 'added' | 'updated' | 'skipped' | 'failed';
  reason?: string;
  entry_id?: number;
  influencer_username?: string;
}

interface ManualStoryBulkResponse {
  processed: number;
  added: number;
  updated: number;
  skipped: number;
  failed: Array<{ url: string; reason: string }>;
  results: ManualStoryBulkResultItem[];
}

interface ManualPostPreview {
  thumbnail_url: string | null;
  caption_preview: string;
  shortcode: string;
  instagram_media_id: string;
  is_reel: boolean;
  permalink: string | null;
  owner_username: string | null;
  owner_username_match: boolean;
  suggested_influencer_id?: number | null;
  suggested_influencer_username?: string | null;
  existing_tracked_post_id?: number | null;
  existing_has_detection?: boolean;
  skipped?: boolean;
  message?: string;
}

interface ManualPostBulkResultItem {
  url: string;
  status: 'added' | 'updated' | 'skipped' | 'failed';
  reason?: string;
  post_id?: number;
  influencer_username?: string;
}

interface ManualPostBulkResponse {
  processed: number;
  added: number;
  updated: number;
  skipped: number;
  failed: Array<{ url: string; reason: string }>;
  results: ManualPostBulkResultItem[];
}

interface ManualPostKeywordState {
  selectedHashtags: string[];
  selectedMentions: string[];
  selectedLinks: string[];
  selectedCaptionKeywords: string[];
}

const looksLikeHttpUrlClient = (s: string) => /^https?:\/\//i.test((s || '').trim());

function splitPostKeywordsForForm(kw: string[]): ManualPostKeywordState {
  const flat: string[] = [];
  (kw || []).forEach((k) => {
    if (typeof k !== 'string') return;
    const t = k.trim();
    if (t) flat.push(t);
  });
  return {
    selectedHashtags: [],
    selectedMentions: [],
    selectedLinks: [],
    selectedCaptionKeywords: flat
  };
}

type SettingsState = {
  target_hashtags: string[];
  target_mentions: string[];
  target_links: LinkTarget[];
  content_tracking_mode: ContentTrackingMode;
  feed_post_limit: number;
  target_caption_keywords: string[];
  auto_check_enabled: boolean;
  auto_check_interval: number;
  auto_check_start_time: string;
  auto_check_end_time: string;
  check_only_undetected: boolean;
};

const padTimeComponent = (value: string) => value.padStart(2, '0');

const extractHourMinute = (value: string | null | undefined) => {
  if (!value) {
    return { hours: '00', minutes: '00' };
  }
  const parts = `${value}`.split(':');
  const hours = padTimeComponent(parts[0] || '00');
  const minutes = padTimeComponent(parts[1] || '00');
  return { hours, minutes };
};

const formatTimeForInput = (value: string | null | undefined) => {
  const { hours, minutes } = extractHourMinute(value);
  return `${hours}:${minutes}`;
};

const normalizeTimeForApi = (value: string | null | undefined) => {
  const { hours, minutes } = extractHourMinute(value);
  const seconds = '00';
  return `${hours}:${minutes}:${seconds}`;
};

const parseTimeToMinutes = (value: string | null | undefined) => {
  if (!value) return null;
  const { hours, minutes } = extractHourMinute(value);
  const total = parseInt(hours, 10) * 60 + parseInt(minutes, 10);
  return Number.isNaN(total) ? null : total;
};

const formatTimeRangeDisplay = (start: string | null | undefined, end: string | null | undefined) => {
  const startFormatted = formatTimeForInput(start ?? '00:00');
  const endFormatted = formatTimeForInput(end ?? '23:59');
  return `${startFormatted} - ${endFormatted}`;
};

const INSTAGRAM_RESERVED_PATHS = new Set(['p', 'reel', 'reels', 'explore', 'tv', 'accounts', 'legal', 'stories', 'share', 'direct']);

/** Toplu influencer ekleme: profil / story paylaşım linkinden kullanıcı adı */
const parseInfluencerLineForBulkAdd = (line: string): string | null => {
  const trimmed = line.trim();
  if (!trimmed) return null;
  if (trimmed.includes('instagram.com/')) {
    const storyUser = trimmed.match(/instagram\.com\/stories\/([^/?\s#]+)/i);
    if (storyUser) {
      const u = storyUser[1].replace('@', '');
      return /^[a-zA-Z0-9._]+$/.test(u) ? u : null;
    }
    const m = trimmed.match(/instagram\.com\/([^/?\s#]+)/i);
    if (m) {
      const segment = m[1].replace('@', '');
      if (INSTAGRAM_RESERVED_PATHS.has(segment.toLowerCase())) return null;
      return /^[a-zA-Z0-9._]+$/.test(segment) ? segment : null;
    }
    return null;
  }
  if (trimmed.startsWith('@')) {
    const username = trimmed.substring(1);
    if (/^[a-zA-Z0-9._]+$/.test(username) && !username.includes(' ')) return username;
    return null;
  }
  if (/^[a-zA-Z0-9._]+$/.test(trimmed) && !trimmed.includes(' ')) return trimmed;
  return null;
};

/** Manuel kayıtlar için 19 haneli stabil path segmenti (URL her yüklemede aynı kalır) */
function manualStoryRowIdToPathId(rowId: number): string {
  const n = BigInt(rowId);
  const mod = BigInt('10000000000000000000');
  const x = (n * BigInt(1103515245) + BigInt(12345)) % mod;
  return x.toString().padStart(19, '0');
}

function pathIdForInstagramStoryLink(story: Story): string {
  const manual = story.story_type === 'manual' || Boolean(story.is_manual);
  if (manual) {
    return manualStoryRowIdToPathId(story.id);
  }
  const digits = String(story.story_id ?? '').replace(/\D/g, '');
  if (digits.length >= 19) return digits.slice(0, 19);
  if (digits.length >= 10) return digits.padStart(19, '0').slice(0, 19);
  if (digits.length > 0) return digits.padStart(19, '0');
  return manualStoryRowIdToPathId(story.id);
}

function buildInstagramStoryWebUrl(username: string, story: Story): string {
  const u = username.replace(/^@/, '').trim();
  const pid = pathIdForInstagramStoryLink(story);
  return `https://www.instagram.com/stories/${encodeURIComponent(u)}/${pid}`;
}

const formatDateTimeLocal = (value?: string | Date | null) => {
  const date = value ? new Date(value) : new Date();
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const createEmptyManualForm = (): ManualEntryFormState => ({
  influencer_id: '',
  story_timestamp: formatDateTimeLocal(),
  story_type: 'manual',
  media_url: '',
  file: null,
  selectedHashtags: [],
  selectedMentions: [],
  selectedLinks: []
});

const parseUtcDateString = (value: string | null | undefined) => {
  if (!value) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isAcceptedManualMimeType = (mime: string) => {
  return mime.startsWith('image/') || mime.startsWith('video/');
};

const isWithinTimeWindow = (
  start: string | null | undefined,
  end: string | null | undefined,
  reference: Date
) => {
  const startMinutes = parseTimeToMinutes(start) ?? 0;
  const endMinutes = parseTimeToMinutes(end) ?? (24 * 60 - 1);
  const currentMinutes = reference.getHours() * 60 + reference.getMinutes();

  if (startMinutes === endMinutes) {
    return true;
  }

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

// Pulse animasyonu
const pulseAnimation = keyframes`
  0% {
    background-color: rgba(25, 118, 210, 0.08);
  }
  50% {
    background-color: rgba(25, 118, 210, 0.16);
  }
  100% {
    background-color: rgba(25, 118, 210, 0.08);
  }
`;

const responsiveTableContainerSx = {
  display: 'block',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch'
} as const;

const responsiveCardSx = {
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden'
} as const;

const responsiveCardContentSx = {
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  p: { xs: 2, sm: 3 },
  '&:last-child': {
    pb: { xs: 2, sm: 3 }
  }
} as const;

const responsiveDialogActionsSx = {
  px: { xs: 2, sm: 3 },
  pb: { xs: 2, sm: 3 },
  gap: 1,
  flexWrap: 'wrap',
  '& .MuiButton-root': {
    width: { xs: '100%', sm: 'auto' }
  }
} as const;

const responsiveDialogContentSx = {
  minWidth: 0,
  overflowX: 'hidden',
  px: { xs: 2, sm: 3 },
  '& .MuiFormControl-root, & .MuiTextField-root': {
    minWidth: 0,
    maxWidth: '100%'
  },
  '& .MuiChip-root': {
    maxWidth: '100%'
  },
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
} as const;

const mobileButtonSx = {
  width: { xs: '100%', sm: 'auto' },
  whiteSpace: 'normal',
  textAlign: 'center'
} as const;

const wrapAnywhereSx = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word'
} as const;

const truncateCellTextSx = {
  minWidth: 0,
  maxWidth: { xs: 160, sm: 220 },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as const;

const responsiveTabsSx = {
  maxWidth: '100%',
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important'
  },
  '& .MuiTab-root': {
    minWidth: { xs: 'auto', sm: 90 },
    px: { xs: 1.5, sm: 2 },
    whiteSpace: 'nowrap'
  }
} as const;

// Emoji ve özel karakterleri temizleme fonksiyonu
const cleanText = (text: string | null | undefined): string => {
  if (!text) return '';
  
  // Emojileri ve özel karakterleri kaldır
  return text
    // U+2600–U+27BF aralığındaki semboller (ES6 'u' bayrağı gerektirmez)
    .replace(/[\u2600-\u27BF]/g, '')
    // Astral düzlemdeki çoğu emoji (surrogate pair eşleşmesi, 'u' gerekmez)
    .replace(/[\uD83C-\uDBFF][\uDC00-\uDFFF]/g, '')
    .replace(/[\u0300-\u036f]/g, '') // Combining karakterler
    .replace(/[^\x20-\x7E\u00A0-\u024F\u0400-\u04FF]/g, '') // Temel Latin, Latin Extended, Cyrillic dışındakileri kaldır
    .trim();
};

const trackedPostHasDownloadableFiles = (post: TrackedPost): boolean => {
  const paths = Array.isArray(post.local_media_paths) ? post.local_media_paths : [];
  return Boolean(post.local_media_path?.trim()) || paths.length > 0;
};

function taskQuotaStatusLabel(status: Influencer['task_status']): string {
  switch (status) {
    case 'completed':
      return 'Tamamlandı';
    case 'in_progress':
      return 'Devam ediyor';
    default:
      return 'Kota yok';
  }
}

/** Metin state'i CampaignDetail dışında tutulur; her tuşta dev sayfa yeniden çizilmez (donma önlenir). */
const AddInfluencerDialog = React.memo(function AddInfluencerDialog({
  open,
  onClose,
  onSubmit,
  submitting = false,
  contentTrackingMode = 'story'
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    links: string,
    defaultQuotas: { task_quota_stories: number; task_quota_posts: number }
  ) => void | Promise<void>;
  submitting?: boolean;
  contentTrackingMode?: ContentTrackingMode;
}) {
  const [draft, setDraft] = useState('');
  const [defStories, setDefStories] = useState(0);
  const [defPosts, setDefPosts] = useState(0);
  useEffect(() => {
    if (open) {
      setDraft('');
      setDefStories(0);
      setDefPosts(0);
    }
  }, [open]);

  const mode = contentTrackingMode || 'story';
  const disableStoryQ = mode === 'feed';
  const disablePostQ = mode === 'story';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Influencer Ekle (Toplu)</DialogTitle>
      <DialogContent sx={responsiveDialogContentSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          Yeni eklenen influencerlara uygulanacak varsayılan görev kotası (yalnızca bu seferde kampanyaya ilk kez bağlananlar):
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Varsayılan hedef hikâye"
              inputProps={{ min: 0, max: 999 }}
              value={defStories}
              disabled={disableStoryQ}
              onChange={(e) =>
                setDefStories(Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0)))
              }
              helperText={disableStoryQ ? 'Bu modda hikâye kotası kullanılmaz' : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Varsayılan hedef gönderi / Reels"
              inputProps={{ min: 0, max: 999 }}
              value={defPosts}
              disabled={disablePostQ}
              onChange={(e) =>
                setDefPosts(Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0)))
              }
              helperText={disablePostQ ? 'Bu modda gönderi kotası kullanılmaz' : ' '}
            />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Her satıra bir Instagram profil linki veya kullanıcı adı girin:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={10}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`https://www.instagram.com/kullanici1/\nhttps://www.instagram.com/stories/kullanici2/123456\n@kullanici3\nkullanici4`}
          helperText="Instagram profil linki veya sadece kullanıcı adı girebilirsiniz"
        />
      </DialogContent>
      <DialogActions sx={responsiveDialogActionsSx}>
        <Button onClick={onClose} disabled={submitting}>İptal</Button>
        <Button
          onClick={() =>
            void onSubmit(draft, { task_quota_stories: defStories, task_quota_posts: defPosts })
          }
          variant="contained"
          color="primary"
          disabled={submitting}
        >
          Influencer'ları Ekle
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingInfluencers, setAddingInfluencers] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [openInfluencerDialog, setOpenInfluencerDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [settings, setSettings] = useState<SettingsState>({
    target_hashtags: [] as string[],
    target_mentions: [] as string[],
    target_links: [] as LinkTarget[],
    content_tracking_mode: 'story',
    feed_post_limit: 12,
    target_caption_keywords: [] as string[],
    auto_check_enabled: false,
    auto_check_interval: 360,
    auto_check_start_time: '00:00',
    auto_check_end_time: '23:59',
    check_only_undetected: false
  });
  const [newLinkMatchType, setNewLinkMatchType] = useState<'contains' | 'exact'>('contains');
  const [intervalValue, setIntervalValue] = useState(6);
  const [intervalUnit, setIntervalUnit] = useState<'minutes' | 'hours'>('hours');
  const [newHashtag, setNewHashtag] = useState('');
  const [newMention, setNewMention] = useState('');
  const [newCaptionKeyword, setNewCaptionKeyword] = useState('');
  const [newLink, setNewLink] = useState('');
  const [fetchingStories, setFetchingStories] = useState(false);
  const [deletingStoryId, setDeletingStoryId] = useState<number | null>(null);
  const [selectedInfluencers, setSelectedInfluencers] = useState<number[]>([]);
  const [processingInfluencers, setProcessingInfluencers] = useState<number[]>([]);
  const [processedInfluencers, setProcessedInfluencers] = useState<number[]>([]);
  const [errorInfluencers, setErrorInfluencers] = useState<{[key: number]: string}>({});
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const stopProcessingRef = React.useRef(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [sortField, setSortField] = useState<'name' | 'username' | 'followers' | 'stories' | 'posts' | 'lastStory' | 'added' | 'updated'>('followers');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // ZIP indirme progress dialog
  const [zipDownloadDialog, setZipDownloadDialog] = useState(false);
  const [zipModalContext, setZipModalContext] = useState<'stories' | 'posts'>('stories');
  const [zipProgress, setZipProgress] = useState({ step: '', percent: 0 });

  const [bulkQuotaDialogOpen, setBulkQuotaDialogOpen] = useState(false);
  const [bulkQuotaStories, setBulkQuotaStories] = useState(0);
  const [bulkQuotaPosts, setBulkQuotaPosts] = useState(0);
  const [quotaDraft, setQuotaDraft] = useState<Record<number, { s: number; p: number }>>({});
  const [quotaSavingId, setQuotaSavingId] = useState<number | null>(null);
  const [quotaEditCell, setQuotaEditCell] = useState<{ id: number; field: 's' | 'p' } | null>(null);
  
  // Public report paylaşma
  const [shareDialog, setShareDialog] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareEnabled, setShareEnabled] = useState(false);
  const [sharePasswordEnabled, setSharePasswordEnabled] = useState(false);
  const [shareSlug, setShareSlug] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);

  // Pagination kaldırıldı - tüm veriler gösterilecek

  // Profil düzenleme
  const [editProfileDialog, setEditProfileDialog] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    instagram_username: '',
    full_name: '',
    bio: '',
    follower_count: 0,
    is_private: false
  });
  // Manuel kayıtlar
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([]);
  const [manualEntriesLoading, setManualEntriesLoading] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualForm, setManualForm] = useState<ManualEntryFormState>(() => createEmptyManualForm());
  const [editingManualEntryId, setEditingManualEntryId] = useState<number | null>(null);
  const [manualFormError, setManualFormError] = useState<string | null>(null);
  const [manualFeedback, setManualFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [manualFileInputKey, setManualFileInputKey] = useState(() => Date.now());
  const [manualRecordKind, setManualRecordKind] = useState<'story' | 'post'>('story');
  const [manualStoryUrl, setManualStoryUrl] = useState('');
  const [manualStoryPreview, setManualStoryPreview] = useState<ManualStoryPreview | null>(null);
  const [manualStoryPreviewLoading, setManualStoryPreviewLoading] = useState(false);
  const [manualPostUrl, setManualPostUrl] = useState('');
  const [manualPostInfluencerId, setManualPostInfluencerId] = useState<number | ''>('');
  const [manualPostPreview, setManualPostPreview] = useState<ManualPostPreview | null>(null);
  const [manualPostPreviewLoading, setManualPostPreviewLoading] = useState(false);
  const [manualPostKw, setManualPostKw] = useState<ManualPostKeywordState>({
    selectedHashtags: [],
    selectedMentions: [],
    selectedLinks: [],
    selectedCaptionKeywords: []
  });
  const [manualTrackedPosts, setManualTrackedPosts] = useState<TrackedPost[]>([]);
  const [manualTrackedPostsLoading, setManualTrackedPostsLoading] = useState(false);
  const [manualPostSubmitting, setManualPostSubmitting] = useState(false);
  const [editingManualPostId, setEditingManualPostId] = useState<number | null>(null);
  const [manualPostConfirmAssign, setManualPostConfirmAssign] = useState(false);
  const [manualStoryEntryMode, setManualStoryEntryMode] = useState<'link' | 'upload'>('link');
  const [manualStoryLinkMode, setManualStoryLinkMode] = useState<'single' | 'bulk'>('single');
  const [manualStoryBulkUrls, setManualStoryBulkUrls] = useState('');
  const [manualStoryBulkLoading, setManualStoryBulkLoading] = useState(false);
  const [manualStoryBulkProgress, setManualStoryBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [manualStoryBulkResult, setManualStoryBulkResult] = useState<ManualStoryBulkResponse | null>(null);
  const [manualPostEntryMode, setManualPostEntryMode] = useState<'link' | 'upload'>('link');
  const [manualPostLinkMode, setManualPostLinkMode] = useState<'single' | 'bulk'>('single');
  const [manualPostBulkUrls, setManualPostBulkUrls] = useState('');
  const [manualPostBulkLoading, setManualPostBulkLoading] = useState(false);
  const [manualPostBulkProgress, setManualPostBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [manualPostBulkResult, setManualPostBulkResult] = useState<ManualPostBulkResponse | null>(null);
  const [manualPostFiles, setManualPostFiles] = useState<File[]>([]);
  const [manualPostSourceUrl, setManualPostSourceUrl] = useState('');
  const [manualPostNote, setManualPostNote] = useState('');
  const [manualPostPostedAt, setManualPostPostedAt] = useState('');
  const [manualPostFileInputKey, setManualPostFileInputKey] = useState(() => Date.now());

  const nonSharingInfluencers = useMemo(() => {
    if (!campaign || !campaign.influencers) return [];
    return campaign.influencers.filter(inf => (inf.detected_story_count || 0) === 0);
  }, [campaign]);

  const nonSharingPostInfluencers = useMemo(() => {
    if (!campaign || !campaign.influencers) return [];
    if ((campaign.content_tracking_mode || 'story') === 'story') return [];
    return campaign.influencers.filter((inf) => (inf.detected_post_count || 0) === 0);
  }, [campaign]);

  const getManualStoryTypeLabel = (type: ManualMediaType) => {
    if (type === 'video') return 'Video';
    if (type === 'image') return 'Görsel';
    return 'Manuel';
  };

  const getManualKeywordChipColor = (keyword: string): 'default' | 'primary' | 'secondary' | 'info' | 'success' => {
    if (keyword.startsWith('#')) return 'primary';
    if (keyword.startsWith('@')) return 'secondary';
    if (looksLikeHttpUrlClient(keyword)) return 'info';
    return 'success';
  };

  const [exportFeedback, setExportFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [serverTime, setServerTime] = useState<Date | null>(null);

  const currentReferenceTime = useMemo(() => serverTime || new Date(), [serverTime]);
  const campaignScheduleInfo = useMemo(() => {
    if (!campaign) {
      return {
        withinWindow: true,
        range: formatTimeRangeDisplay(null, null)
      };
    }

    return {
      withinWindow: isWithinTimeWindow(campaign.auto_check_start_time, campaign.auto_check_end_time, currentReferenceTime),
      range: formatTimeRangeDisplay(campaign.auto_check_start_time, campaign.auto_check_end_time)
    };
  }, [campaign, currentReferenceTime]);

  const lastAutoCheckDate = useMemo(() => {
    if (!campaign?.last_auto_check) return null;
    return parseUtcDateString(campaign.last_auto_check);
  }, [campaign?.last_auto_check]);

  const autoCheckCampaignCopy = useMemo(
    () => getAutoCheckSectionCopy(campaign?.content_tracking_mode),
    [campaign?.content_tracking_mode]
  );

  const autoCheckSettingsCopy = useMemo(
    () => getAutoCheckSectionCopy(settings.content_tracking_mode),
    [settings.content_tracking_mode]
  );

  // sortedInfluencers ve paginatedInfluencers kaldırıldı - artık sortedInfluencersMain ve paginatedInfluencersMain kullanılıyor


  useEffect(() => {
    if (!serverTime) return;
    const timer = setInterval(() => {
      setServerTime(prev => (prev ? new Date(prev.getTime() + 60000) : prev));
    }, 60000);
    return () => clearInterval(timer);
  }, [serverTime]);

  const fetchCampaign = useCallback(async () => {
    if (!id || id === 'undefined' || isNaN(Number(id))) {
      setError('Kampanya ID bulunamadı');
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`/api/campaigns/${id}`);
      const data = response.data;
      
      // JSON alanlarını parse et (try-catch ile optimize - hata durumunda boş array döndür)
      let target_hashtags = [];
      let target_mentions = [];
      let target_links = [];
      
      try {
        target_hashtags = typeof data.target_hashtags === 'string' 
          ? JSON.parse(data.target_hashtags || '[]') 
          : (data.target_hashtags || []);
      } catch { target_hashtags = []; }
      
      try {
        target_mentions = typeof data.target_mentions === 'string' 
          ? JSON.parse(data.target_mentions || '[]') 
          : (data.target_mentions || []);
      } catch { target_mentions = []; }
      
      try {
        target_links = typeof data.target_links === 'string' 
          ? JSON.parse(data.target_links || '[]') 
          : (data.target_links || []);
      } catch { target_links = []; }

      let target_caption_keywords: string[] = [];
      try {
        const rawCap = (data as any).target_caption_keywords;
        target_caption_keywords = typeof rawCap === 'string'
          ? JSON.parse(rawCap || '[]')
          : (Array.isArray(rawCap) ? rawCap : []);
        if (!Array.isArray(target_caption_keywords)) target_caption_keywords = [];
        target_caption_keywords = target_caption_keywords.map((s) => String(s || '').trim()).filter(Boolean);
      } catch {
        target_caption_keywords = [];
      }

      const feedLimitRaw = (data as any).feed_post_limit;
      const feed_post_limit = Math.min(50, Math.max(1, Number(feedLimitRaw) > 0 ? Number(feedLimitRaw) : 12));
      
      const parsedData = {
        ...data,
        target_hashtags,
        target_mentions,
        target_links,
        target_caption_keywords,
        feed_post_limit,
        auto_check_start_time: formatTimeForInput(data.auto_check_start_time),
        auto_check_end_time: formatTimeForInput(data.auto_check_end_time)
      };
      
      setCampaign(parsedData);
      
      // Auto check interval'i saat/dakikaya çevir
      const minutes = parsedData.auto_check_interval || 360;
      if (minutes >= 60 && minutes % 60 === 0) {
        setIntervalValue(minutes / 60);
        setIntervalUnit('hours');
      } else {
        setIntervalValue(minutes);
        setIntervalUnit('minutes');
      }
      
      // Geriye uyumluluk: Eski string[] formatını LinkTarget[] formatına çevir
      const normalizeLinks = (links: any): LinkTarget[] => {
        if (!links || !Array.isArray(links)) return [];
        return links.map(link => {
          if (typeof link === 'string') {
            // Eski format: string → LinkTarget'a çevir (default: contains)
            return { url: link, matchType: 'contains' as 'contains' | 'exact' };
          } else {
            // Yeni format: zaten LinkTarget
            return link;
          }
        });
      };
      
      setSettings({
        target_hashtags: parsedData.target_hashtags,
        target_mentions: parsedData.target_mentions,
        target_links: normalizeLinks(parsedData.target_links),
        content_tracking_mode: (parsedData.content_tracking_mode as ContentTrackingMode) || 'story',
        feed_post_limit: parsedData.feed_post_limit ?? 12,
        target_caption_keywords: parsedData.target_caption_keywords || [],
        auto_check_enabled: parsedData.auto_check_enabled || false,
        auto_check_interval: parsedData.auto_check_interval || 360,
        auto_check_start_time: formatTimeForInput(parsedData.auto_check_start_time),
        auto_check_end_time: formatTimeForInput(parsedData.auto_check_end_time),
        check_only_undetected: parsedData.check_only_undetected || false
      });
      
      setServerTime(data.server_time ? new Date(data.server_time) : new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kampanya yüklenemedi');
    } finally {
      setLoading(false);
      setServerTime(prev => prev || new Date());
    }
  }, [id]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  useEffect(() => {
    if (!campaign?.influencers?.length) {
      setQuotaDraft({});
      return;
    }
    const d: Record<number, { s: number; p: number }> = {};
    campaign.influencers.forEach((inf) => {
      d[inf.id] = {
        s: Math.min(999, Math.max(0, Number(inf.task_quota_stories ?? 0))),
        p: Math.min(999, Math.max(0, Number(inf.task_quota_posts ?? 0)))
      };
    });
    setQuotaDraft(d);
  }, [campaign]);

  const saveInfluencerQuota = useCallback(
    async (infId: number) => {
      if (!id) return;
      const draft = quotaDraft[infId];
      if (!draft) return;
      const inf = campaign?.influencers.find((i) => i.id === infId);
      if (!inf) return;
      const prevS = Math.min(999, Math.max(0, Number(inf.task_quota_stories ?? 0)));
      const prevP = Math.min(999, Math.max(0, Number(inf.task_quota_posts ?? 0)));
      if (draft.s === prevS && draft.p === prevP) return;
      setQuotaSavingId(infId);
      try {
        const detached = inf.is_profile_detached === true || infId < 0;
        if (detached && inf.campaign_influencer_id != null) {
          await axios.patch(
            `/api/campaigns/${id}/campaign-influencers/${inf.campaign_influencer_id}/task-quotas`,
            {
              task_quota_stories: draft.s,
              task_quota_posts: draft.p
            }
          );
        } else {
          await axios.patch(`/api/campaigns/${id}/influencers/${infId}/task-quotas`, {
            task_quota_stories: draft.s,
            task_quota_posts: draft.p
          });
        }
        await fetchCampaign();
      } catch (e: any) {
        setError(e.response?.data?.message || 'Kota kaydedilemedi');
      } finally {
        setQuotaSavingId(null);
      }
    },
    [id, quotaDraft, campaign?.influencers, fetchCampaign]
  );

  const handleBulkTaskQuotaApply = useCallback(async () => {
    if (!id || selectedInfluencers.length === 0) return;
    try {
      const detachedCiIds = selectedInfluencers
        .filter((x) => x < 0)
        .map((x) => Math.abs(x));
      const activeIds = selectedInfluencers.filter((x) => x > 0);
      for (const ciRowId of detachedCiIds) {
        await axios.patch(`/api/campaigns/${id}/campaign-influencers/${ciRowId}/task-quotas`, {
          task_quota_stories: bulkQuotaStories,
          task_quota_posts: bulkQuotaPosts
        });
      }
      if (activeIds.length > 0) {
        await axios.post(`/api/campaigns/${id}/influencers/bulk-task-quotas`, {
          influencer_ids: activeIds,
          task_quota_stories: bulkQuotaStories,
          task_quota_posts: bulkQuotaPosts
        });
      }
      setBulkQuotaDialogOpen(false);
      await fetchCampaign();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Toplu kota uygulanamadı');
    }
  }, [id, selectedInfluencers, bulkQuotaStories, bulkQuotaPosts, fetchCampaign]);

  const fetchManualEntries = useCallback(async () => {
    if (!id) return;
    setManualEntriesLoading(true);
    try {
      const response = await axios.get(`/api/campaigns/${id}/manual-entries`);
      const entries = Array.isArray(response.data) ? response.data : [];
      setManualEntries((entries as any[]).map((entry) => {
        const safeEntry = entry as ManualEntry;
        const keywords = Array.isArray(safeEntry.detected_keywords) ? safeEntry.detected_keywords : [];
        const hashtags = Array.isArray(safeEntry.detected_hashtags) ? safeEntry.detected_hashtags : [];
        const mentions = Array.isArray(safeEntry.detected_mentions) ? safeEntry.detected_mentions : [];
        const links = Array.isArray(safeEntry.detected_links) ? safeEntry.detected_links : [];
        const storyType: ManualMediaType = safeEntry.story_type === 'video' || safeEntry.story_type === 'image' ? safeEntry.story_type : 'manual';
        return {
          ...safeEntry,
          story_type: storyType,
          detected_keywords: keywords,
          detected_hashtags: hashtags,
          detected_mentions: mentions,
          detected_links: links
        } as ManualEntry;
      }));
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Manuel kayıtlar yüklenemedi',
        severity: 'error'
      });
    } finally {
      setManualEntriesLoading(false);
    }
  }, [id]);

  const resetManualForm = useCallback(() => {
    setManualForm(createEmptyManualForm());
    setManualStoryUrl('');
    setManualStoryPreview(null);
    setManualStoryEntryMode('link');
    setManualStoryLinkMode('single');
    setManualStoryBulkUrls('');
    setManualStoryBulkProgress(null);
    setManualStoryBulkResult(null);
    setManualFormError(null);
    setEditingManualEntryId(null);
    setManualFileInputKey(Date.now());
  }, []);

  const handleManualEdit = (entry: ManualEntry) => {
    setManualForm({
      influencer_id: entry.influencer_id,
      story_timestamp: formatDateTimeLocal(entry.story_timestamp || undefined),
      story_type: entry.story_type,
      media_url: entry.media_url || '',
      file: null,
      selectedHashtags: entry.detected_hashtags || [],
      selectedMentions: entry.detected_mentions || [],
      selectedLinks: entry.detected_links || []
    });
    setManualStoryUrl(entry.story_url || '');
    setManualStoryPreview(null);
    setManualStoryEntryMode(entry.story_url?.trim() ? 'link' : 'upload');
    setEditingManualEntryId(entry.id);
    setManualFormError(null);
    setManualFileInputKey(Date.now());
  };

  const handleManualFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    if (file && !isAcceptedManualMimeType(file.type)) {
      setManualFormError('Sadece görsel veya video dosyaları yükleyebilirsiniz.');
      setManualFeedback({ open: true, message: 'Geçersiz dosya formatı', severity: 'error' });
      setManualForm(prev => ({ ...prev, file: null }));
      setManualFileInputKey(Date.now());
      return;
    }

    setManualForm(prev => ({
      ...prev,
      file,
      story_type: file
        ? file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('image/')
            ? 'image'
            : prev.story_type
        : prev.story_type
    }));
  };

  const handleManualStoryPreview = async () => {
    if (!id) return;
    if (!manualStoryUrl.trim()) {
      setManualFeedback({ open: true, message: 'Hikaye linki gerekli', severity: 'error' });
      return;
    }
    setManualStoryPreviewLoading(true);
    setManualStoryPreview(null);
    try {
      const payload = { story_url: manualStoryUrl.trim() };
      const { data } = await axios.post(`/api/campaigns/${id}/manual-entries/preview`, payload);
      setManualStoryPreview(data);
      if (data?.suggested_influencer_id) {
        setManualForm(prev => ({
          ...prev,
          influencer_id: Number(data.suggested_influencer_id)
        }));
      }
      if (data?.story_type === 'image' || data?.story_type === 'video') {
        setManualForm(prev => ({
          ...prev,
          story_type: data.story_type
        }));
      }
      if (data?.story_timestamp) {
        const ts = String(data.story_timestamp);
        const localValue = ts.includes('T') ? ts.slice(0, 16) : ts;
        setManualForm(prev => ({
          ...prev,
          story_timestamp: localValue,
          media_url: data.media_url || prev.media_url
        }));
      } else if (data?.media_url) {
        setManualForm(prev => ({
          ...prev,
          media_url: data.media_url
        }));
      }
      if (data?.message) {
        const severity =
          data.skipped ? 'info' : data.download_status === 'failed' ? 'info' : 'success';
        setManualFeedback({
          open: true,
          message: data.message,
          severity
        });
      }
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Önizleme alınamadı',
        severity: 'error'
      });
    } finally {
      setManualStoryPreviewLoading(false);
    }
  };

  const parseManualStoryBulkUrls = (text: string) =>
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

  const handleManualStoryBulk = async () => {
    if (!id) return;
    const urls = parseManualStoryBulkUrls(manualStoryBulkUrls);
    if (urls.length === 0) {
      setManualFeedback({ open: true, message: 'En az bir hikaye linki girin', severity: 'error' });
      return;
    }
    setManualStoryBulkLoading(true);
    setManualStoryBulkResult(null);
    setManualStoryBulkProgress({ current: 0, total: urls.length });
    try {
      const { data } = await axios.post<ManualStoryBulkResponse>(
        `/api/campaigns/${id}/manual-entries/bulk`,
        { story_urls: urls },
        { timeout: LONG_RUNNING_HTTP_MS }
      );
      setManualStoryBulkResult(data);
      setManualStoryBulkProgress({ current: data.processed, total: data.processed });
      const summary = `${data.added} eklendi, ${data.updated} güncellendi, ${data.skipped} atlandı, ${data.failed.length} hata`;
      setManualFeedback({
        open: true,
        message: `Toplu işlem tamamlandı: ${summary}`,
        severity: data.failed.length > 0 ? 'info' : 'success'
      });
      if (data.added > 0 || data.updated > 0) {
        await fetchManualEntries();
        try {
          await fetchCampaign();
        } catch (campaignError) {
          console.warn('Kampanya verisi yenilenemedi:', (campaignError as Error).message);
        }
      }
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Toplu işlem başarısız',
        severity: 'error'
      });
    } finally {
      setManualStoryBulkLoading(false);
    }
  };

  const handleManualSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id) return;

    const isEditing = Boolean(editingManualEntryId);

    const selectedInfluencerId = typeof manualForm.influencer_id === 'string'
      ? Number(manualForm.influencer_id)
      : manualForm.influencer_id;
    const previewInfluencerId = manualStoryPreview?.suggested_influencer_id
      ? Number(manualStoryPreview.suggested_influencer_id)
      : NaN;
    const linkModeUsesPreview =
      !isEditing && manualStoryEntryMode === 'link' && Boolean(manualStoryPreview);
    const influencerId = linkModeUsesPreview && previewInfluencerId && !Number.isNaN(previewInfluencerId)
      ? previewInfluencerId
      : selectedInfluencerId && !Number.isNaN(selectedInfluencerId)
        ? selectedInfluencerId
        : previewInfluencerId;
    const storyUrl = manualStoryUrl.trim();
    const hasExistingMedia = Boolean(manualForm.media_url);

    if (!manualForm.story_timestamp) {
      setManualFormError('Lütfen paylaşım tarihini girin.');
      setManualFeedback({ open: true, message: 'Paylaşım tarihi zorunludur', severity: 'error' });
      return;
    }

    if (!isEditing && manualStoryEntryMode === 'upload') {
      if (!selectedInfluencerId || Number.isNaN(selectedInfluencerId)) {
        const message = 'Dosya yükleme modunda influencer seçimi zorunludur';
        setManualFormError(message);
        setManualFeedback({ open: true, message, severity: 'error' });
        return;
      }
      if (!manualForm.file && !hasExistingMedia) {
        setManualFormError('Lütfen medya dosyası yükleyin.');
        setManualFeedback({ open: true, message: 'Medya dosyası zorunludur', severity: 'error' });
        return;
      }
    } else if (!isEditing && manualStoryEntryMode === 'link') {
      if (!storyUrl) {
        const message = 'Linkten getir modunda Instagram hikaye linki zorunludur.';
        setManualFormError(message);
        setManualFeedback({ open: true, message, severity: 'error' });
        return;
      }
      if (!manualStoryPreview) {
        const message = 'Kaydetmeden önce "Getir (önizleme)" ile hikaye linkini doğrulayın.';
        setManualFormError(message);
        setManualFeedback({ open: true, message, severity: 'error' });
        return;
      }
    }

    if (!influencerId || Number.isNaN(influencerId)) {
      const message = storyUrl
        ? 'Linkten influencer otomatik bulunamadı. Lütfen influencer seçip tekrar deneyin.'
        : 'Influencer seçimi zorunludur';
      setManualFormError(message);
      setManualFeedback({ open: true, message, severity: 'error' });
      return;
    }

    if (isEditing && !manualForm.file && !hasExistingMedia && !storyUrl) {
      setManualFormError('Lütfen medya dosyası yükleyin veya hikaye linki girin.');
      setManualFeedback({ open: true, message: 'Medya dosyası veya hikaye linki zorunludur', severity: 'error' });
      return;
    }

    setManualSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('influencer_id', influencerId.toString());
      formData.append('story_timestamp', manualForm.story_timestamp);
      const resolvedStoryType =
        manualForm.story_type === 'manual' &&
        (manualStoryPreview?.story_type === 'image' || manualStoryPreview?.story_type === 'video')
          ? manualStoryPreview.story_type
          : manualForm.story_type;
      formData.append('story_type', resolvedStoryType);
      formData.append('selectedHashtags', JSON.stringify(manualForm.selectedHashtags));
      formData.append('selectedMentions', JSON.stringify(manualForm.selectedMentions));
      formData.append('selectedLinks', JSON.stringify(manualForm.selectedLinks));
      if (storyUrl && (isEditing || manualStoryEntryMode === 'link')) {
        formData.append('story_url', storyUrl);
      }
      if (manualForm.file) {
        formData.append('media', manualForm.file);
      } else if (manualForm.media_url) {
        formData.append('media_url', manualForm.media_url);
      } else if (manualStoryPreview?.media_url) {
        formData.append('media_url', manualStoryPreview.media_url);
      }

      if (isEditing && editingManualEntryId) {
        await axios.put(`/api/campaigns/${id}/manual-entries/${editingManualEntryId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setManualFeedback({ open: true, message: 'Manuel kayıt güncellendi', severity: 'success' });
      } else {
        const response = await axios.post(`/api/campaigns/${id}/manual-entries`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setManualFeedback({
          open: true,
          message: response.data?.message || 'Manuel kayıt eklendi',
          severity: response.data?.skipped ? 'info' : response.data?.download_warning ? 'info' : 'success'
        });
      }

      await fetchManualEntries();
      try {
        await fetchCampaign();
      } catch (campaignError) {
        console.warn('Kampanya verisi yenilenemedi:', (campaignError as Error).message);
      }
      resetManualForm();
      setManualFileInputKey(Date.now());
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Manuel kayıt kaydedilemedi';
      setManualFormError(message);
      setManualFeedback({ open: true, message, severity: 'error' });
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleManualDelete = async (entryId: number) => {
    if (!id) return;
    const confirm = window.confirm('Bu manuel kaydı silmek istediğinize emin misiniz?');
    if (!confirm) return;

    try {
      await axios.delete(`/api/campaigns/${id}/manual-entries/${entryId}`);
      setManualFeedback({ open: true, message: 'Manuel kayıt silindi', severity: 'success' });
      if (editingManualEntryId === entryId) {
        resetManualForm();
      }
      await fetchManualEntries();
      try {
        await fetchCampaign();
      } catch (campaignError) {
        console.warn('Kampanya verisi yenilenemedi:', (campaignError as Error).message);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Manuel kayıt silinemedi';
      setManualFeedback({ open: true, message, severity: 'error' });
    }
  };

  const handleManualFeedbackClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setManualFeedback(prev => ({ ...prev, open: false }));
  };

  const fetchManualTrackedPosts = useCallback(async () => {
    if (!id) return;
    setManualTrackedPostsLoading(true);
    try {
      const response = await axios.get(`/api/campaigns/${id}/manual-tracked-posts`);
      const rows = Array.isArray(response.data) ? response.data : [];
      setManualTrackedPosts(rows as TrackedPost[]);
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Manuel gönderiler yüklenemedi',
        severity: 'error'
      });
    } finally {
      setManualTrackedPostsLoading(false);
    }
  }, [id]);

  const resetManualPostForm = useCallback(() => {
    setManualPostUrl('');
    setManualPostInfluencerId('');
    setManualPostPreview(null);
    setManualPostConfirmAssign(false);
    setManualPostEntryMode('link');
    setManualPostLinkMode('single');
    setManualPostBulkUrls('');
    setManualPostBulkResult(null);
    setManualPostBulkProgress(null);
    setManualPostFiles([]);
    setManualPostSourceUrl('');
    setManualPostNote('');
    setManualPostPostedAt('');
    setManualPostFileInputKey(Date.now());
    setManualPostKw({
      selectedHashtags: [],
      selectedMentions: [],
      selectedLinks: [],
      selectedCaptionKeywords: []
    });
    setEditingManualPostId(null);
  }, []);

  const handleManualPostPreview = async () => {
    if (!id) return;
    const infId = typeof manualPostInfluencerId === 'number' ? manualPostInfluencerId : Number(manualPostInfluencerId);
    if (!manualPostUrl.trim()) {
      setManualFeedback({ open: true, message: 'Gönderi linki gerekli', severity: 'error' });
      return;
    }
    setManualPostPreviewLoading(true);
    setManualPostPreview(null);
    try {
      const payload: Record<string, string | number> = { url: manualPostUrl.trim() };
      if (infId && !Number.isNaN(infId)) {
        payload.influencer_id = infId;
      }
      const { data } = await axios.post(`/api/campaigns/${id}/manual-tracked-posts/preview`, payload);
      setManualPostPreview(data);
      if (data?.suggested_influencer_id) {
        setManualPostInfluencerId(Number(data.suggested_influencer_id));
      }
      setManualPostConfirmAssign(false);
      if (data?.message) {
        setManualFeedback({
          open: true,
          message: data.message,
          severity: data.skipped ? 'info' : 'success'
        });
      }
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Önizleme alınamadı',
        severity: 'error'
      });
    } finally {
      setManualPostPreviewLoading(false);
    }
  };

  const parseManualPostBulkUrls = (text: string) =>
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

  const handleManualPostBulk = async () => {
    if (!id) return;
    const urls = parseManualPostBulkUrls(manualPostBulkUrls);
    if (urls.length === 0) {
      setManualFeedback({ open: true, message: 'En az bir gönderi linki girin', severity: 'error' });
      return;
    }
    setManualPostBulkLoading(true);
    setManualPostBulkResult(null);
    setManualPostBulkProgress({ current: 0, total: urls.length });
    try {
      const { data } = await axios.post<ManualPostBulkResponse>(
        `/api/campaigns/${id}/manual-tracked-posts/bulk`,
        { post_urls: urls },
        { timeout: LONG_RUNNING_HTTP_MS }
      );
      setManualPostBulkResult(data);
      setManualPostBulkProgress({ current: data.processed, total: data.processed });
      const summary = `${data.added} eklendi, ${data.updated} güncellendi, ${data.skipped} atlandı, ${data.failed.length} hata`;
      setManualFeedback({
        open: true,
        message: `Toplu işlem tamamlandı: ${summary}`,
        severity: data.failed.length > 0 ? 'info' : 'success'
      });
      if (data.added > 0 || data.updated > 0) {
        await fetchManualTrackedPosts();
        try {
          await fetchCampaign();
        } catch (campaignError) {
          console.warn('Kampanya verisi yenilenemedi:', (campaignError as Error).message);
        }
      }
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Toplu işlem başarısız',
        severity: 'error'
      });
    } finally {
      setManualPostBulkLoading(false);
    }
  };

  const handleManualPostSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id) return;
    const selectedInfId = typeof manualPostInfluencerId === 'number' ? manualPostInfluencerId : Number(manualPostInfluencerId);
    const previewInfId = manualPostPreview?.suggested_influencer_id ? Number(manualPostPreview.suggested_influencer_id) : NaN;
    const infId = selectedInfId && !Number.isNaN(selectedInfId) ? selectedInfId : previewInfId;

    if (!editingManualPostId && manualPostEntryMode === 'upload') {
      if (!infId || Number.isNaN(infId)) {
        setManualFeedback({ open: true, message: 'Influencer seçimi zorunludur', severity: 'error' });
        return;
      }
      if (!manualPostFiles.length) {
        setManualFeedback({ open: true, message: 'En az bir görsel veya video dosyası seçin', severity: 'error' });
        return;
      }
      setManualPostSubmitting(true);
      try {
        const fd = new FormData();
        fd.append('influencer_id', String(infId));
        manualPostFiles.forEach((f) => fd.append('files', f));
        fd.append('selectedCaptionKeywords', JSON.stringify(manualPostKw.selectedCaptionKeywords));
        if (manualPostSourceUrl.trim()) fd.append('source_url', manualPostSourceUrl.trim());
        if (manualPostNote.trim()) fd.append('note', manualPostNote.trim());
        if (manualPostPostedAt.trim()) fd.append('posted_at', manualPostPostedAt.trim());
        await axios.post(`/api/campaigns/${id}/manual-tracked-posts/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setManualFeedback({ open: true, message: 'Manuel gönderi (dosya) eklendi', severity: 'success' });
        resetManualPostForm();
        await fetchManualTrackedPosts();
        await fetchCampaign();
      } catch (err: any) {
        setManualFeedback({
          open: true,
          message: err?.response?.data?.message || 'Yükleme başarısız',
          severity: 'error'
        });
      } finally {
        setManualPostSubmitting(false);
      }
      return;
    }

    if (editingManualPostId && (!infId || Number.isNaN(infId))) {
      setManualFeedback({ open: true, message: 'Influencer seçimi zorunludur', severity: 'error' });
      return;
    }

    if (!editingManualPostId && !manualPostUrl.trim()) {
      setManualFeedback({ open: true, message: 'Gönderi linki gerekli', severity: 'error' });
      return;
    }
    if (!editingManualPostId && manualPostUrl.trim() && !manualPostPreview) {
      setManualFeedback({
        open: true,
        message: 'Kaydetmeden önce "Getir (önizleme)" ile gönderiyi doğrulayın.',
        severity: 'error'
      });
      return;
    }
    if (!editingManualPostId && (!infId || Number.isNaN(infId))) {
      setManualFeedback({
        open: true,
        message: 'Linkten influencer otomatik bulunamadı. Lütfen influencer seçip tekrar deneyin.',
        severity: 'error'
      });
      return;
    }
    if (
      !editingManualPostId &&
      manualPostPreview &&
      manualPostPreview.owner_username_match === false &&
      !manualPostConfirmAssign
    ) {
      setManualFeedback({
        open: true,
        message:
          'Bu gönderi seçilen influencer hesabına ait görünmüyor. Yine de kaydetmek için aşağıdaki onayı işaretleyin.',
        severity: 'error'
      });
      return;
    }
    setManualPostSubmitting(true);
    try {
      if (editingManualPostId) {
        await axios.put(`/api/campaigns/${id}/manual-tracked-posts/${editingManualPostId}`, {
          influencer_id: infId,
          selectedCaptionKeywords: manualPostKw.selectedCaptionKeywords
        });
        setManualFeedback({ open: true, message: 'Manuel gönderi güncellendi', severity: 'success' });
        resetManualPostForm();
      } else {
        const response = await axios.post(`/api/campaigns/${id}/manual-tracked-posts`, {
          url: manualPostUrl.trim(),
          influencer_id: infId,
          selectedCaptionKeywords: manualPostKw.selectedCaptionKeywords,
          confirm_assign_to_selected_influencer:
            manualPostPreview?.owner_username_match === false ? manualPostConfirmAssign : false
        });
        setManualFeedback({
          open: true,
          message: response.data?.message || 'Manuel gönderi eklendi',
          severity: response.data?.skipped ? 'info' : 'success'
        });
        resetManualPostForm();
      }
      await fetchManualTrackedPosts();
      await fetchCampaign();
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Kayıt başarısız',
        severity: 'error'
      });
    } finally {
      setManualPostSubmitting(false);
    }
  };

  const handleEditManualPost = (post: TrackedPost) => {
    const kwsRaw = post.detected_keywords;
    const kws =
      typeof kwsRaw === 'string'
        ? (() => {
            try {
              return JSON.parse(kwsRaw || '[]');
            } catch {
              return [];
            }
          })()
        : Array.isArray(kwsRaw)
          ? kwsRaw
          : [];
    setManualPostKw(splitPostKeywordsForForm(kws));
    setManualPostUrl(post.permalink || '');
    setManualPostInfluencerId(post.influencer_id);
    setManualPostPreview(null);
    setManualPostEntryMode('link');
    setEditingManualPostId(post.id);
    setManualRecordKind('post');
  };

  const handleDeleteManualPost = async (postId: number) => {
    if (!id) return;
    const confirmDelete = window.confirm('Bu manuel gönderiyi silmek istediğinize emin misiniz?');
    if (!confirmDelete) return;
    try {
      await axios.delete(`/api/campaigns/${id}/manual-tracked-posts/${postId}`);
      setManualFeedback({ open: true, message: 'Silindi', severity: 'success' });
      if (editingManualPostId === postId) {
        resetManualPostForm();
      }
      await fetchManualTrackedPosts();
      await fetchCampaign();
    } catch (err: any) {
      setManualFeedback({
        open: true,
        message: err?.response?.data?.message || 'Silinemedi',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchManualEntries();
    fetchManualTrackedPosts();
  }, [id, fetchManualEntries, fetchManualTrackedPosts]);

  const runProfileUpdateForIds = useCallback(async (
    influencerIds: number[],
    options?: { check_only_undetected?: boolean }
  ) => {
    if (!id) return;
    if (influencerIds.length === 0) return;

    const checkOnly = options?.check_only_undetected ?? false;

    setFetchingStories(true);
    setProcessedInfluencers([]);
    setErrorInfluencers({});
    stopProcessingRef.current = false;

    try {
      const batchSize = MANUAL_HTTP_BATCH_SIZE;
      const batches = [];
      for (let i = 0; i < influencerIds.length; i += batchSize) {
        batches.push(influencerIds.slice(i, i + batchSize));
      }

      setBatchProgress({ current: 0, total: batches.length });
      let totalUpdated = 0;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        if (stopProcessingRef.current) {
          await fetchCampaign();
          alert(`⚠️ İşlem durduruldu!\n\n✅ ${totalUpdated} profil güncellendi`);
          break;
        }

        const batch = batches[batchIndex];
        setProcessingInfluencers(batch);
        setBatchProgress({ current: batchIndex + 1, total: batches.length });

        try {
          const response = await axios.post(`/api/campaigns/${id}/update-profiles`, {
            influencer_ids: batch,
            check_only_undetected: checkOnly
          }, { timeout: LONG_RUNNING_HTTP_MS });

          totalUpdated += response.data.updated_count || 0;
          setProcessedInfluencers(prev => [...prev, ...batch]);
          await fetchCampaign();
        } catch (batchError: any) {
          console.error('Profil güncelleme batch hatası:', batchError);
          batch.forEach(infId => {
            setErrorInfluencers(prev => ({
              ...prev,
              [infId]: batchError.response?.data?.message || 'API hatası'
            }));
            setProcessedInfluencers(prev => prev.filter(pid => pid !== infId));
          });
        }

        setProcessingInfluencers([]);
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      await fetchCampaign();
      setSelectedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });

      if (totalUpdated > 0) {
        alert(`✅ Profil Güncelleme Tamamlandı!\n\n${totalUpdated} influencer profili güncellendi`);
      } else {
        alert(`⚠️ Profil Güncelleme Tamamlandı!\n\n0 influencer profili güncellendi\n\nTüm hesaplar için API'den veri alınamadı. Kullanıcı adları değişmiş olabilir.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Profiller güncellenemedi');
    } finally {
      setFetchingStories(false);
      setProcessingInfluencers([]);
      setProcessedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      stopProcessingRef.current = false;
    }
  }, [id, fetchCampaign]);

  const handleAddInfluencers = async (
    influencerLinks: string,
    defaultQuotas?: { task_quota_stories: number; task_quota_posts: number }
  ) => {
    try {
      // Her satırı işle ve sadece geçerli Instagram profil bilgilerini al
      const lines = influencerLinks.split('\n');
      const validLinks: string[] = [];
      
      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const parsed = parseInfluencerLineForBulkAdd(trimmed);
        if (parsed) {
          validLinks.push(parsed);
          return;
        }
        console.log(`Geçersiz satır atlandı: "${trimmed}"`);
      });
      
      if (validLinks.length === 0) {
        setError('Geçerli Instagram profili bulunamadı.\n\nGeçerli formatlar:\n- https://www.instagram.com/kullanici/\n- https://www.instagram.com/stories/kullanici/...\n- @kullanici\n- kullanici');
        return;
      }

      setAddingInfluencers(true);

      const addRes = await axios.post(`/api/campaigns/${id}/add-influencers`, {
        instagram_links: validLinks,
        ...(defaultQuotas
          ? {
              default_task_quota_stories: defaultQuotas.task_quota_stories,
              default_task_quota_posts: defaultQuotas.task_quota_posts
            }
          : {})
      });

      setOpenInfluencerDialog(false);
      
      // Kampanyayı yenile (tabloya eklenecek)
      await fetchCampaign();
      
      const skippedCount = lines.filter(l => l.trim()).length - validLinks.length;
      const message = skippedCount > 0 
        ? `✅ ${validLinks.length} influencer eklendi!\n⚠️ ${skippedCount} geçersiz satır atlandı`
        : `✅ ${validLinks.length} influencer başarıyla eklendi!`;
      
      alert(message);

      const newIds: number[] = Array.isArray(addRes.data?.new_influencer_ids)
        ? addRes.data.new_influencer_ids.map((n: unknown) => Number(n)).filter((n: number) => !Number.isNaN(n))
        : [];
      if (newIds.length > 0) {
        await runProfileUpdateForIds(newIds, { check_only_undetected: false });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Influencer\'lar eklenemedi');
    } finally {
      setAddingInfluencers(false);
    }
  };

  const handleRemoveInfluencer = async (influencerId: number) => {
    if (influencerId < 0) {
      const ciId = Math.abs(influencerId);
      if (
        !window.confirm(
          'Bu satır (profili silinmiş influencer) kampanya listesinden kaldırılacak. Geçmiş tespitler silinmez. Devam edilsin mi?'
        )
      ) {
        return;
      }
      try {
        await axios.delete(`/api/campaigns/${id}/campaign-influencers/${ciId}`);
        fetchCampaign();
        alert('Satır kampanya listesinden kaldırıldı; tespit kayıtları duruyor.');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Kaldırılamadı');
      }
      return;
    }

    if (!window.confirm('Bu influencer\'ı kampanyadan kaldırmak istediğinize emin misiniz?\n\nBu influencer\'a ait tespit edilen story\'ler de silinecektir.')) {
      return;
    }

    try {
      // Kampanyadan influencer'ı ve story'lerini kaldır
      await axios.delete(`/api/campaigns/${id}/influencers/${influencerId}`);
      
      fetchCampaign();
      alert('Influencer ve tespit edilen story\'leri kampanyadan kaldırıldı');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Influencer kaldırılamadı');
    }
  };

  // Profil düzenleme fonksiyonları
  const handleEditProfile = (influencer: any) => {
    setEditingInfluencer(influencer);
    setEditFormData({
      instagram_username: influencer.instagram_username || '',
      full_name: influencer.full_name || '',
      bio: influencer.bio || '',
      follower_count: influencer.follower_count || 0,
      is_private: influencer.is_private || false
    });
    setEditProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!editingInfluencer) return;

    try {
      await axios.put(`/api/influencers/${editingInfluencer.id}`, editFormData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setEditProfileDialog(false);
      setEditingInfluencer(null);
      fetchCampaign(); // Kampanya verilerini yenile
      alert('Profil başarıyla güncellendi');
    } catch (error: any) {
      console.error('Profil güncelleme hatası:', error);
      alert(error.response?.data?.message || 'Profil güncellenirken hata oluştu');
    }
  };

  const handleCancelEdit = () => {
    setEditProfileDialog(false);
    setEditingInfluencer(null);
    setEditFormData({
      instagram_username: '',
      full_name: '',
      bio: '',
      follower_count: 0,
      is_private: false
    });
  };

  const handleRemoveSelected = async () => {
    if (selectedInfluencers.length === 0) {
      alert('Lütfen silmek için en az bir influencer seçin');
      return;
    }

    const detachedIds = selectedInfluencers.filter((x) => x < 0).map((x) => Math.abs(x));
    const activeIds = selectedInfluencers.filter((x) => x > 0);
    const msg =
      detachedIds.length > 0 && activeIds.length > 0
        ? `${activeIds.length} aktif influencer için tespitler silinecek; ${detachedIds.length} profil-silinmiş satır yalnızca listeden kalkacak (tespitler kalır). Onaylıyor musunuz?`
        : detachedIds.length > 0
          ? `${detachedIds.length} satır kampanya listesinden kaldırılacak; geçmiş tespitler silinmez. Onaylıyor musunuz?`
          : `${activeIds.length} influencer'ı kampanyadan kaldırmak istediğinize emin misiniz?\n\nBu influencer'lara ait tespit edilen story'ler de silinecektir.`;

    if (!window.confirm(msg)) {
      return;
    }

    try {
      setLoading(true);
      
      let totalRemoved = 0;
      const deleteErrors: { batch: number; count: number; error: string }[] = [];

      for (const ciId of detachedIds) {
        try {
          await axios.delete(`/api/campaigns/${id}/campaign-influencers/${ciId}`);
          totalRemoved += 1;
        } catch (e: any) {
          deleteErrors.push({ batch: 0, count: 1, error: e.response?.data?.message || 'Kaldırma hatası' });
        }
      }

      if (activeIds.length > 0) {
        const batchSize = 20;
        const batches: number[][] = [];
        for (let i = 0; i < activeIds.length; i += batchSize) {
          batches.push(activeIds.slice(i, i + batchSize));
        }

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          try {
            const response = await axios.post(`/api/campaigns/${id}/remove-influencers`, {
              influencer_ids: batch
            });
            totalRemoved += response.data.removed_count || batch.length;
          } catch (batchError: any) {
            console.error(`Silme batch ${batchIndex + 1} hatası:`, batchError);
            deleteErrors.push({
              batch: batchIndex + 1,
              count: batch.length,
              error: batchError.response?.data?.message || 'Silme hatası'
            });
          }
          if (batchIndex < batches.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }
      }

      setSelectedInfluencers([]);
      await fetchCampaign();
      
      if (deleteErrors.length > 0) {
        alert(`⚠️ Kısmi Başarı!\n\n✅ İşlem sayısı: ${totalRemoved}\n❌ ${deleteErrors.length} adımda hata oluştu`);
      } else {
        alert(`✅ İşlem tamamlandı (${totalRemoved} kayıt işlendi)`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Influencer\'lar kaldırılamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // Interval'i dakikaya çevir
      const intervalInMinutes = intervalUnit === 'hours' 
        ? intervalValue * 60 
        : intervalValue;
      
      const startTimeForApi = normalizeTimeForApi(settings.auto_check_start_time);
      const endTimeForApi = normalizeTimeForApi(settings.auto_check_end_time);
      
      await axios.put(`/api/campaigns/${id}`, {
        target_hashtags: settings.target_hashtags,
        target_mentions: settings.target_mentions,
        target_links: settings.target_links, // Artık LinkTarget[] formatında
        content_tracking_mode: settings.content_tracking_mode,
        feed_post_limit: Math.min(50, Math.max(1, Number(settings.feed_post_limit) || 12)),
        target_caption_keywords: settings.target_caption_keywords,
        auto_check_enabled: settings.auto_check_enabled,
        auto_check_interval: intervalInMinutes,
        auto_check_start_time: startTimeForApi,
        auto_check_end_time: endTimeForApi,
        check_only_undetected: settings.check_only_undetected
      });
      setOpenSettingsDialog(false);
      fetchCampaign();
      alert('✅ Ayarlar kaydedildi!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ayarlar kaydedilemedi');
    }
  };

  const handleAddHashtag = () => {
    if (newHashtag.trim()) {
      setSettings({
        ...settings,
        target_hashtags: [...settings.target_hashtags, newHashtag.trim().replace(/^#/, '')]
      });
      setNewHashtag('');
    }
  };

  const handleRemoveHashtag = (index: number) => {
    setSettings({
      ...settings,
      target_hashtags: settings.target_hashtags.filter((_, i) => i !== index)
    });
  };

  const handleAddMention = () => {
    if (newMention.trim()) {
      setSettings({
        ...settings,
        target_mentions: [...settings.target_mentions, newMention.trim().replace(/^@/, '')]
      });
      setNewMention('');
    }
  };

  const handleRemoveMention = (index: number) => {
    setSettings({
      ...settings,
      target_mentions: settings.target_mentions.filter((_, i) => i !== index)
    });
  };

  const handleAddLink = () => {
    if (newLink.trim()) {
      setSettings({
        ...settings,
        target_links: [...settings.target_links, { url: newLink.trim(), matchType: newLinkMatchType }]
      });
      setNewLink('');
      setNewLinkMatchType('contains'); // Reset to default
    }
  };

  const handleRemoveLink = (index: number) => {
    setSettings({
      ...settings,
      target_links: settings.target_links.filter((_, i) => i !== index)
    });
  };

  const handleAddCaptionKeyword = () => {
    const t = newCaptionKeyword.trim();
    if (!t) return;
    setSettings({
      ...settings,
      target_caption_keywords: [...settings.target_caption_keywords, t]
    });
    setNewCaptionKeyword('');
  };

  const handleRemoveCaptionKeyword = (index: number) => {
    setSettings({
      ...settings,
      target_caption_keywords: settings.target_caption_keywords.filter((_, i) => i !== index)
    });
  };

  const handleUpdateLinkMatchType = (index: number, matchType: 'contains' | 'exact') => {
    const updatedLinks = [...settings.target_links];
    updatedLinks[index] = { ...updatedLinks[index], matchType };
    setSettings({
      ...settings,
      target_links: updatedLinks
    });
  };

  const handleUpdateProfiles = async () => {
    if (!campaign) return;

    const raw =
      selectedInfluencers.length > 0
        ? selectedInfluencers
        : campaign.influencers.map((inf) => inf.id);
    const influencerIds = raw.filter((x) => x > 0);
    if (influencerIds.length === 0) {
      alert('Profil güncellenecek aktif influencer yok (profili kaldırılmış satırlar atlanır).');
      return;
    }

    await runProfileUpdateForIds(influencerIds, {
      check_only_undetected: campaign?.check_only_undetected || false
    });
  };

  const handleFetchStories = async () => {
    if (!campaign) return;
    
    const raw =
      selectedInfluencers.length > 0
        ? selectedInfluencers
        : campaign.influencers.map((inf) => inf.id);
    const influencerIds = raw.filter((x) => x > 0);
    if (influencerIds.length === 0) {
      alert('Hikaye çekilecek aktif influencer yok (profili kaldırılmış satırlar atlanır).');
      return;
    }
    
    setFetchingStories(true);
    setProcessedInfluencers([]);
    setErrorInfluencers({});
    stopProcessingRef.current = false;
    
    try {
      const batchSize = MANUAL_HTTP_BATCH_SIZE;
      const batches = [];
      for (let i = 0; i < influencerIds.length; i += batchSize) {
        batches.push(influencerIds.slice(i, i + batchSize));
      }

      setBatchProgress({ current: 0, total: batches.length });

      let totalResults = {
        total_influencers: 0,
        total_fetched: 0,
        total_saved: 0,
        total_rechecked: 0
      };

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        if (stopProcessingRef.current) {
          await fetchCampaign();
          const errorCount = Object.keys(errorInfluencers).length;
          const successCount = processedInfluencers.length - errorCount;
          alert(`⚠️ İşlem durduruldu!\n\n✅ Başarılı: ${successCount} influencer işlendi\n❌ Hatalı: ${errorCount}\n🚫 İptal: ${batches.length - batchIndex} batch\n\n💾 Tüm değişiklikler kaydedildi!`);
          break;
        }

        const batch = batches[batchIndex];
        setProcessingInfluencers(batch);
        setBatchProgress({ current: batchIndex + 1, total: batches.length });
        
        console.log(`Frontend: Story Batch ${batchIndex + 1}/${batches.length} işleniyor`);
        
        try {
          const response = await axios.post(`/api/campaigns/${id}/fetch-stories`, {
            influencer_ids: batch,
            recheck_stories: true,
            update_profiles: false,  // Sadece story'leri çek
            check_only_undetected: campaign?.check_only_undetected || false,
            batch_info: `${batchIndex + 1}/${batches.length}`
          }, { timeout: LONG_RUNNING_HTTP_MS });

          totalResults.total_influencers += response.data.total_influencers || 0;
          totalResults.total_fetched += response.data.total_fetched || 0;
          totalResults.total_saved += response.data.total_saved || 0;
          totalResults.total_rechecked += response.data.total_rechecked || 0;

          setProcessedInfluencers(prev => [...prev, ...batch]);
          await fetchCampaign();
          
        } catch (batchError: any) {
          console.error(`Batch ${batchIndex + 1} hatası:`, batchError);
          batch.forEach(infId => {
            setErrorInfluencers(prev => ({
              ...prev,
              [infId]: batchError.response?.data?.message || 'API hatası'
            }));
            setProcessedInfluencers(prev => prev.filter(pid => pid !== infId));
          });
        }

        setProcessingInfluencers([]);
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      await fetchCampaign();
      setSelectedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      
      const errorCount = Object.keys(errorInfluencers).length;
      const successCount = totalResults.total_influencers - errorCount;
      
      alert(`✅ Hikaye Kontrolü Tamamlandı!\n\n✅ Başarılı: ${successCount}\n❌ Hatalı: ${errorCount}\n\n${totalResults.total_fetched} story çekildi\n${totalResults.total_saved} story kaydedildi\n${totalResults.total_rechecked} eski story yeniden analiz edildi`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Story\'ler çekilirken hata oluştu');
    } finally {
      setFetchingStories(false);
      setProcessingInfluencers([]);
      setProcessedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      stopProcessingRef.current = false;
    }
  };

  const handleFetchPosts = async () => {
    if (!campaign) return;

    const raw =
      selectedInfluencers.length > 0
        ? selectedInfluencers
        : campaign.influencers.map((inf) => inf.id);
    const influencerIds = raw.filter((x) => x > 0);
    if (influencerIds.length === 0) {
      alert('Gönderi çekilecek aktif influencer yok (profili kaldırılmış satırlar atlanır).');
      return;
    }

    setFetchingStories(true);
    setProcessedInfluencers([]);
    setErrorInfluencers({});
    stopProcessingRef.current = false;

    try {
      const batchSize = MANUAL_HTTP_BATCH_SIZE;
      const batches = [];
      for (let i = 0; i < influencerIds.length; i += batchSize) {
        batches.push(influencerIds.slice(i, i + batchSize));
      }

      setBatchProgress({ current: 0, total: batches.length });

      let totalResults = {
        total_influencers: 0,
        total_fetched: 0,
        total_saved: 0,
        total_rechecked: 0
      };

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        if (stopProcessingRef.current) {
          await fetchCampaign();
          const errorCount = Object.keys(errorInfluencers).length;
          const successCount = processedInfluencers.length - errorCount;
          alert(`⚠️ İşlem durduruldu!\n\n✅ Başarılı: ${successCount} influencer işlendi\n❌ Hatalı: ${errorCount}\n🚫 İptal: ${batches.length - batchIndex} batch\n\n💾 Tüm değişiklikler kaydedildi!`);
          break;
        }

        const batch = batches[batchIndex];
        setProcessingInfluencers(batch);
        setBatchProgress({ current: batchIndex + 1, total: batches.length });

        try {
          const response = await axios.post(
            `/api/campaigns/${id}/fetch-posts`,
            {
              influencer_ids: batch,
              recheck_posts: true,
              update_profiles: false,
              check_only_undetected: campaign?.check_only_undetected || false,
              batch_info: `${batchIndex + 1}/${batches.length}`
            },
            { timeout: LONG_RUNNING_HTTP_MS }
          );

          totalResults.total_influencers += response.data.total_influencers || 0;
          totalResults.total_fetched += response.data.total_fetched || 0;
          totalResults.total_saved += response.data.total_saved || 0;
          totalResults.total_rechecked += response.data.total_rechecked || 0;

          setProcessedInfluencers((prev) => [...prev, ...batch]);
          await fetchCampaign();
        } catch (batchError: any) {
          console.error(`Gönderi batch ${batchIndex + 1} hatası:`, batchError);
          batch.forEach((infId) => {
            setErrorInfluencers((prev) => ({
              ...prev,
              [infId]: batchError.response?.data?.message || 'API hatası'
            }));
            setProcessedInfluencers((prev) => prev.filter((pid) => pid !== infId));
          });
        }

        setProcessingInfluencers([]);
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      await fetchCampaign();
      setSelectedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });

      const errorCount = Object.keys(errorInfluencers).length;
      const successCount = totalResults.total_influencers - errorCount;

      alert(
        `✅ Gönderi / Reels kontrolü tamamlandı!\n\n✅ Başarılı: ${successCount}\n❌ Hatalı: ${errorCount}\n\n${totalResults.total_fetched} öğe çekildi\n${totalResults.total_saved} kayıt eklendi\n${totalResults.total_rechecked} kayıt yeniden analiz edildi`
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gönderiler çekilirken hata oluştu');
    } finally {
      setFetchingStories(false);
      setProcessingInfluencers([]);
      setProcessedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      stopProcessingRef.current = false;
    }
  };

  const handleFullUpdate = async () => {
    if (!campaign) return;
    
    const raw =
      selectedInfluencers.length > 0
        ? selectedInfluencers
        : campaign.influencers.map((inf) => inf.id);
    const influencerIds = raw.filter((x) => x > 0);
    if (influencerIds.length === 0) {
      alert('İşlem yapılacak aktif influencer yok (profili kaldırılmış satırlar atlanır).');
      return;
    }
    
    setFetchingStories(true);
    setProcessedInfluencers([]);
    setErrorInfluencers({});
    stopProcessingRef.current = false;
    
    try {
      const batchSize = MANUAL_HTTP_BATCH_SIZE;
      const batches = [];
      for (let i = 0; i < influencerIds.length; i += batchSize) {
        batches.push(influencerIds.slice(i, i + batchSize));
      }

      setBatchProgress({ current: 0, total: batches.length });

      let totalResults = {
        total_influencers: 0,
        total_fetched: 0,
        total_saved: 0,
        total_rechecked: 0,
        total_profiles_updated: 0
      };

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        if (stopProcessingRef.current) {
          await fetchCampaign();
          const errorCount = Object.keys(errorInfluencers).length;
          const successCount = processedInfluencers.length - errorCount;
          alert(`⚠️ İşlem durduruldu!\n\n✅ Başarılı: ${successCount} influencer işlendi\n❌ Hatalı: ${errorCount}\n🚫 İptal: ${batches.length - batchIndex} batch\n\n💾 Tüm değişiklikler kaydedildi!`);
          break;
        }

        const batch = batches[batchIndex];
        setProcessingInfluencers(batch);
        setBatchProgress({ current: batchIndex + 1, total: batches.length });
        
        console.log(`Frontend: Tam Güncelleme Batch ${batchIndex + 1}/${batches.length}`);

        const mode = campaign.content_tracking_mode || 'story';

        try {
          if (mode === 'feed') {
            const response = await axios.post(`/api/campaigns/${id}/fetch-posts`, {
              influencer_ids: batch,
              recheck_posts: true,
              update_profiles: true,
              check_only_undetected: campaign?.check_only_undetected || false,
              batch_info: `${batchIndex + 1}/${batches.length}`
            }, { timeout: LONG_RUNNING_HTTP_MS });

            totalResults.total_influencers += response.data.total_influencers || 0;
            totalResults.total_fetched += response.data.total_fetched || 0;
            totalResults.total_saved += response.data.total_saved || 0;
            totalResults.total_rechecked += response.data.total_rechecked || 0;
            totalResults.total_profiles_updated += response.data.total_profiles_updated || 0;
          } else if (mode === 'both') {
            const storyRes = await axios.post(`/api/campaigns/${id}/fetch-stories`, {
              influencer_ids: batch,
              recheck_stories: true,
              update_profiles: true,
              check_only_undetected: campaign?.check_only_undetected || false,
              batch_info: `${batchIndex + 1}/${batches.length}`
            }, { timeout: LONG_RUNNING_HTTP_MS });

            totalResults.total_influencers += storyRes.data.total_influencers || 0;
            totalResults.total_fetched += storyRes.data.total_fetched || 0;
            totalResults.total_saved += storyRes.data.total_saved || 0;
            totalResults.total_rechecked += storyRes.data.total_rechecked || 0;
            totalResults.total_profiles_updated += storyRes.data.total_profiles_updated || 0;

            const postRes = await axios.post(`/api/campaigns/${id}/fetch-posts`, {
              influencer_ids: batch,
              recheck_posts: true,
              update_profiles: false,
              check_only_undetected: campaign?.check_only_undetected || false,
              batch_info: `${batchIndex + 1}/${batches.length}`
            }, { timeout: LONG_RUNNING_HTTP_MS });

            totalResults.total_influencers += postRes.data.total_influencers || 0;
            totalResults.total_fetched += postRes.data.total_fetched || 0;
            totalResults.total_saved += postRes.data.total_saved || 0;
            totalResults.total_rechecked += postRes.data.total_rechecked || 0;
            totalResults.total_profiles_updated += postRes.data.total_profiles_updated || 0;
          } else {
            const response = await axios.post(`/api/campaigns/${id}/fetch-stories`, {
              influencer_ids: batch,
              recheck_stories: true,
              update_profiles: true,
              check_only_undetected: campaign?.check_only_undetected || false,
              batch_info: `${batchIndex + 1}/${batches.length}`
            }, { timeout: LONG_RUNNING_HTTP_MS });

            totalResults.total_influencers += response.data.total_influencers || 0;
            totalResults.total_fetched += response.data.total_fetched || 0;
            totalResults.total_saved += response.data.total_saved || 0;
            totalResults.total_rechecked += response.data.total_rechecked || 0;
            totalResults.total_profiles_updated += response.data.total_profiles_updated || 0;
          }

          setProcessedInfluencers(prev => [...prev, ...batch]);
          await fetchCampaign();
          
        } catch (batchError: any) {
          console.error(`Batch ${batchIndex + 1} hatası:`, batchError);
          batch.forEach(infId => {
            setErrorInfluencers(prev => ({
              ...prev,
              [infId]: batchError.response?.data?.message || 'API hatası'
            }));
            setProcessedInfluencers(prev => prev.filter(pid => pid !== infId));
          });
        }

        setProcessingInfluencers([]);
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      await fetchCampaign();
      setSelectedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      
      const errorCount = Object.keys(errorInfluencers).length;
      const successCount = totalResults.total_influencers - errorCount;
      
      alert(`✅ Tam Güncelleme Tamamlandı!\n\n✅ Başarılı: ${successCount}\n❌ Hatalı: ${errorCount}\n\n${totalResults.total_fetched} öğe çekildi\n${totalResults.total_saved} kayıt eklendi\n${totalResults.total_rechecked} yeniden analiz\n${totalResults.total_profiles_updated} profil güncellendi`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Güncelleme hatası');
    } finally {
      setFetchingStories(false);
      setProcessingInfluencers([]);
      setProcessedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      stopProcessingRef.current = false;
    }
  };

  const handleCheckUndetectedOnly = async () => {
    if (!campaign) return;
    
    const raw =
      selectedInfluencers.length > 0
        ? selectedInfluencers
        : campaign.influencers.map((inf) => inf.id);
    const influencerIds = raw.filter((x) => x > 0);
    if (influencerIds.length === 0) {
      alert('Kontrol edilecek aktif influencer yok (profili kaldırılmış satırlar atlanır).');
      return;
    }
    
    setFetchingStories(true);
    setProcessedInfluencers([]);
    setErrorInfluencers({});
    stopProcessingRef.current = false;
    
    try {
      const batchSize = MANUAL_HTTP_BATCH_SIZE;
      const batches = [];
      for (let i = 0; i < influencerIds.length; i += batchSize) {
        batches.push(influencerIds.slice(i, i + batchSize));
      }

      setBatchProgress({ current: 0, total: batches.length });

      let totalResults = {
        total_influencers: 0,
        total_fetched: 0,
        total_saved: 0,
        total_rechecked: 0
      };

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        if (stopProcessingRef.current) {
          await fetchCampaign();
          const errorCount = Object.keys(errorInfluencers).length;
          const successCount = processedInfluencers.length - errorCount;
          alert(`⚠️ İşlem durduruldu!\n\n✅ Başarılı: ${successCount} influencer işlendi\n❌ Hatalı: ${errorCount}\n🚫 İptal: ${batches.length - batchIndex} batch\n\n💾 Tüm değişiklikler kaydedildi!`);
          break;
        }

        const batch = batches[batchIndex];
        setProcessingInfluencers(batch);
        setBatchProgress({ current: batchIndex + 1, total: batches.length });
        
        console.log(`Frontend: Tespit Edilemeyenler Kontrolü Batch ${batchIndex + 1}/${batches.length}`);

        const undetectedMode = campaign.content_tracking_mode || 'story';

        const runUndetectedPosts = async () => {
          const response = await axios.post(`/api/campaigns/${id}/fetch-posts`, {
            influencer_ids: batch,
            recheck_posts: true,
            update_profiles: false,
            check_only_undetected: true,
            batch_info: `${batchIndex + 1}/${batches.length}`
          }, { timeout: LONG_RUNNING_HTTP_MS });
          totalResults.total_influencers += response.data.total_influencers || 0;
          totalResults.total_fetched += response.data.total_fetched || 0;
          totalResults.total_saved += response.data.total_saved || 0;
          totalResults.total_rechecked += response.data.total_rechecked || 0;
        };

        const runUndetectedStories = async () => {
          const response = await axios.post(`/api/campaigns/${id}/fetch-stories`, {
            influencer_ids: batch,
            recheck_stories: true,
            update_profiles: false,
            check_only_undetected: true,
            batch_info: `${batchIndex + 1}/${batches.length}`
          }, { timeout: LONG_RUNNING_HTTP_MS });
          totalResults.total_influencers += response.data.total_influencers || 0;
          totalResults.total_fetched += response.data.total_fetched || 0;
          totalResults.total_saved += response.data.total_saved || 0;
          totalResults.total_rechecked += response.data.total_rechecked || 0;
        };

        try {
          if (undetectedMode === 'feed') {
            await runUndetectedPosts();
          } else if (undetectedMode === 'both') {
            try {
              await runUndetectedStories();
            } catch (e: any) {
              const msg = e.response?.data?.message || '';
              if (!msg.includes('Tespit edilemeyen influencer bulunamadı')) {
                throw e;
              }
            }
            try {
              await runUndetectedPosts();
            } catch (e: any) {
              const msg = e.response?.data?.message || '';
              if (!msg.includes('Tespit edilemeyen influencer bulunamadı')) {
                throw e;
              }
            }
          } else {
            await runUndetectedStories();
          }

          setProcessedInfluencers(prev => [...prev, ...batch]);
          await fetchCampaign();
          
        } catch (batchError: any) {
          console.error(`Batch ${batchIndex + 1} hatası:`, batchError);
          const errorMessage = batchError.response?.data?.message || 'API hatası';
          if (errorMessage.includes('Tespit edilemeyen influencer bulunamadı')) {
            console.log(`Batch ${batchIndex + 1}: Tespit edilmemiş influencer yok (normal durum)`);
          } else {
            batch.forEach(infId => {
              setErrorInfluencers(prev => ({
                ...prev,
                [infId]: errorMessage
              }));
              setProcessedInfluencers(prev => prev.filter(pid => pid !== infId));
            });
          }
        }

        setProcessingInfluencers([]);
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      await fetchCampaign();
      setSelectedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      
      const errorCount = Object.keys(errorInfluencers).length;
      const successCount = totalResults.total_influencers - errorCount;
      
      alert(`✅ Tespit Edilemeyenler Kontrolü Tamamlandı!\n\n✅ Başarılı: ${successCount}\n❌ Hatalı: ${errorCount}\n\n${totalResults.total_fetched} öğe çekildi\n${totalResults.total_saved} kayıt eklendi\n${totalResults.total_rechecked} yeniden analiz`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'İçerik çekilirken hata oluştu');
    } finally {
      setFetchingStories(false);
      setProcessingInfluencers([]);
      setProcessedInfluencers([]);
      setBatchProgress({ current: 0, total: 0 });
      stopProcessingRef.current = false;
    }
  };

  const handleSelectInfluencer = (influencerId: number) => {
    setSelectedInfluencers(prev => {
      if (prev.includes(influencerId)) {
        return prev.filter(id => id !== influencerId);
      } else {
        return [...prev, influencerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInfluencers.length === campaign?.influencers.length) {
      setSelectedInfluencers([]);
    } else {
      setSelectedInfluencers(campaign?.influencers.map(inf => inf.id) || []);
    }
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      // Aynı alana tıklanırsa sıralama yönünü değiştir
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni alana tıklanırsa, o alanı seç ve varsayılan sıralama (desc)
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Optimize edilmiş sıralanmış influencer listesi (useMemo ile cache'lenmiş)
  const sortedInfluencersMain = useMemo(() => {
    if (!campaign?.influencers) return [];
    
    // id'si olmayan influencer'ları filtrele
    const validInfluencers = campaign.influencers.filter(inf => inf && inf.id);
    
    return [...validInfluencers].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'name':
          const nameA = (a.full_name || a.instagram_username).toLowerCase();
          const nameB = (b.full_name || b.instagram_username).toLowerCase();
          compareValue = nameA.localeCompare(nameB, 'tr');
          break;
        case 'username':
          compareValue = a.instagram_username.localeCompare(b.instagram_username, 'tr');
          break;
        case 'followers':
          compareValue = (a.follower_count || 0) - (b.follower_count || 0);
          break;
        case 'stories':
          const storyCountA = a.detected_story_count || 0;
          const storyCountB = b.detected_story_count || 0;
          compareValue = storyCountA - storyCountB;
          break;
        case 'posts':
          compareValue = (a.detected_post_count || 0) - (b.detected_post_count || 0);
          break;
        case 'lastStory':
          const lastStoryA = parseUtcDateString(a.last_detected_at)?.getTime() || 0;
          const lastStoryB = parseUtcDateString(b.last_detected_at)?.getTime() || 0;
          compareValue = lastStoryA - lastStoryB;
          break;
        case 'added':
          compareValue = (parseUtcDateString(a.added_at)?.getTime() || 0) - (parseUtcDateString(b.added_at)?.getTime() || 0);
          break;
        case 'updated':
          const updatedA = parseUtcDateString(a.last_updated)?.getTime() || 0;
          const updatedB = parseUtcDateString(b.last_updated)?.getTime() || 0;
          compareValue = updatedA - updatedB;
          break;
        default:
          compareValue = 0;
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  }, [campaign?.influencers, sortField, sortOrder]);

  // Pagination kaldırıldı - tüm influencer'lar gösterilecek

  const buildNonSharingText = () => {
    if (!nonSharingInfluencers.length) {
      return 'Hiç paylaşım yapmayan influencer bulunmuyor.';
    }

    return nonSharingInfluencers
      .map((inf, index) => {
        const namePart = inf.full_name ? ` - ${cleanText(inf.full_name)}` : '';
        return `${index + 1}. @${inf.instagram_username}${namePart}`;
      })
      .join('\n');
  };

  const handleCopyNonSharingInfluencers = async () => {
    if (!nonSharingInfluencers.length) {
      setExportFeedback({ open: true, message: 'Hiç paylaşım yapmayan influencer bulunmuyor.', severity: 'info' });
      return;
    }

    const text = buildNonSharingText();

    try {
      if (typeof navigator !== 'undefined' && typeof window !== 'undefined' && navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setExportFeedback({ open: true, message: 'Paylaşmayan influencerlar panoya kopyalandı.', severity: 'success' });
        return;
      }

      throw new Error('Clipboard API not available');
    } catch (error) {
      console.error('Panoya kopyalama başarısız:', error);

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        setExportFeedback({
          open: true,
          message: successful ? 'Paylaşmayan influencerlar panoya kopyalandı.' : 'Kopyalama başarısız oldu.',
          severity: successful ? 'success' : 'error'
        });
      } catch (fallbackError) {
        console.error('Yedek kopyalama başarısız:', fallbackError);
        setExportFeedback({ open: true, message: 'Kopyalama başarısız oldu.', severity: 'error' });
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const buildNonSharingPostText = () => {
    if (!nonSharingPostInfluencers.length) {
      return 'Gönderi paylaşımı yapmayan influencer bulunmuyor.';
    }

    return nonSharingPostInfluencers
      .map((inf, index) => {
        const namePart = inf.full_name ? ` - ${cleanText(inf.full_name)}` : '';
        return `${index + 1}. @${inf.instagram_username}${namePart}`;
      })
      .join('\n');
  };

  const handleCopyNonSharingPostInfluencers = async () => {
    if (!nonSharingPostInfluencers.length) {
      setExportFeedback({
        open: true,
        message: 'Gönderi paylaşımı yapmayan influencer bulunmuyor.',
        severity: 'info'
      });
      return;
    }

    const text = buildNonSharingPostText();

    try {
      if (typeof navigator !== 'undefined' && typeof window !== 'undefined' && navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setExportFeedback({
          open: true,
          message: 'Gönderi paylaşmayan influencerlar panoya kopyalandı.',
          severity: 'success'
        });
        return;
      }

      throw new Error('Clipboard API not available');
    } catch (error) {
      console.error('Panoya kopyalama başarısız:', error);

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        setExportFeedback({
          open: true,
          message: successful ? 'Gönderi paylaşmayan influencerlar panoya kopyalandı.' : 'Kopyalama başarısız oldu.',
          severity: successful ? 'success' : 'error'
        });
      } catch (fallbackError) {
        console.error('Yedek kopyalama başarısız:', fallbackError);
        setExportFeedback({ open: true, message: 'Kopyalama başarısız oldu.', severity: 'error' });
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handleExportFeedbackClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setExportFeedback(prev => ({ ...prev, open: false }));
  };

  const handleDownloadAllStories = async () => {
    if (!campaign) return;
    setZipModalContext('stories');
    setZipDownloadDialog(true);
    setZipProgress({ step: '📦 Hazırlanıyor...', percent: 0 });
    
    try {
      const totalStories = campaign.recent_stories?.length || 0;
      
      // Adım 1: Başlatılıyor
      setZipProgress({ step: `📁 ${totalStories} story toplanıyor...`, percent: 20 });
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Adım 2: İşleniyor
      setZipProgress({ step: '🗜️ ZIP arşivi oluşturuluyor...', percent: 40 });
      
      const response = await axios.post('/api/stories/download-campaign', {
        campaign_id: id
      }, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const downloadPercent = 40 + (percentCompleted * 0.5); // 40% ile 90% arası
            setZipProgress({ 
              step: `⬇️ İndiriliyor... ${Math.round(progressEvent.loaded / 1024 / 1024)}MB / ${Math.round(progressEvent.total / 1024 / 1024)}MB`, 
              percent: downloadPercent 
            });
          }
        }
      });

      // Adım 3: Dosya kaydediliyor
      setZipProgress({ step: '💾 Dosya kaydediliyor...', percent: 95 });
      
      // Dosyayı indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${campaign.title}_stories.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Tamamlandı
      setZipProgress({ step: '✅ Tamamlandı!', percent: 100 });
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setZipDownloadDialog(false);
    } catch (err: any) {
      setZipProgress({ step: '❌ Hata oluştu!', percent: 0 });
      await new Promise(resolve => setTimeout(resolve, 2000));
      setZipDownloadDialog(false);
      setError('Story\'ler indirilirken hata oluştu');
    }
  };

  const handleDownloadAllTrackedPosts = async () => {
    if (!campaign) return;
    setZipModalContext('posts');
    setZipDownloadDialog(true);
    setZipProgress({ step: '📦 Hazırlanıyor...', percent: 0 });
    try {
      const totalPosts = campaign.recent_posts?.length || 0;
      setZipProgress({ step: `📁 ${totalPosts} gönderi dosyası toplanıyor...`, percent: 20 });
      await new Promise((resolve) => setTimeout(resolve, 400));
      setZipProgress({ step: '🗜️ ZIP arşivi oluşturuluyor...', percent: 40 });
      const response = await axios.post(
        `/api/campaigns/${id}/download-tracked-posts`,
        {},
        {
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const downloadPercent = 40 + percentCompleted * 0.5;
              setZipProgress({
                step: `⬇️ İndiriliyor... ${Math.round(progressEvent.loaded / 1024 / 1024)}MB / ${Math.round(progressEvent.total / 1024 / 1024)}MB`,
                percent: downloadPercent
              });
            }
          }
        }
      );
      setZipProgress({ step: '💾 Dosya kaydediliyor...', percent: 95 });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] as string | undefined;
      let fname = `${campaign.title}_posts.zip`;
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
        if (m?.[1]) fname = decodeURIComponent(m[1].replace(/['"]/g, ''));
      }
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setZipProgress({ step: '✅ Tamamlandı!', percent: 100 });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setZipDownloadDialog(false);
    } catch (err: any) {
      setZipProgress({ step: '❌ Hata oluştu!', percent: 0 });
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setZipDownloadDialog(false);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.data instanceof Blob ? await err.response.data.text().catch(() => '') : '');
      let parsed = msg;
      try {
        if (msg && msg.startsWith('{')) parsed = JSON.parse(msg).message || msg;
      } catch {
        /* ignore */
      }
      setExportFeedback({
        open: true,
        message: typeof parsed === 'string' && parsed ? parsed : 'Gönderiler indirilirken hata oluştu',
        severity: 'error'
      });
    }
  };

  // Public report paylaşma fonksiyonları
  const handleOpenShareDialog = () => {
    if (campaign) {
      setShareEnabled(campaign.share_enabled || false);
      setSharePassword('');
      setShareUrl(campaign.share_token ? `${window.location.origin}/rapor/${campaign.share_slug || campaign.share_token}` : '');
      setShareSlug(campaign.share_slug || '');
      setSharePasswordEnabled(!!campaign.share_password);
    }
    setShareDialog(true);
  };

  const handleCreateShareLink = async () => {
    try {
      setShareLoading(true);
      const response = await axios.post(`/api/public/campaigns/${id}/share`, {
        slug: shareSlug,
        password_enabled: sharePasswordEnabled,
        password: sharePasswordEnabled ? sharePassword : ''
      });
      
      setShareUrl(response.data.share_url);
      alert('✅ Paylaşım linki oluşturuldu!');
      fetchCampaign(); // Kampanyayı yenile
    } catch (err: any) {
      setError(err.response?.data?.message || 'Paylaşım linki oluşturulamadı');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('📋 Link kopyalandı!');
  };

  const handleDownloadTrackedPostFile = async (postId: number) => {
    try {
      const response = await axios.get(`/api/campaigns/${id}/tracked-posts/${postId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] as string | undefined;
      let fileName = `gonderi-${postId}`;
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
        if (m?.[1]) fileName = decodeURIComponent(m[1].replace(/['"]/g, ''));
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setExportFeedback({
        open: true,
        message: err?.response?.data?.message || 'Dosya indirilemedi',
        severity: 'error'
      });
    }
  };

  const handleDownloadSingleStory = async (storyId: number, story: Story) => {
    try {
      const response = await axios.get(`/api/stories/${storyId}/download`, {
        responseType: 'blob'
      });

      // Dosya adını story bilgisinden oluştur
      if (!story.detected_at) {
        setExportFeedback({ open: true, message: 'Story tarihi bulunamadı', severity: 'error' });
        return;
      }
      
      const storyDate = new Date(story.detected_at);
      if (isNaN(storyDate.getTime())) {
        setExportFeedback({ open: true, message: 'Geçersiz story tarihi', severity: 'error' });
        return;
      }
      
      const date = storyDate.toISOString().split('T')[0];
      
      // Aynı kullanıcının aynı gündeki story sırasını bul
      const sameUserStories = campaign?.recent_stories.filter(s => {
        if (!s.detected_at) return false;
        const sDate = new Date(s.detected_at);
        if (isNaN(sDate.getTime())) return false;
        return s.instagram_username === story.instagram_username &&
               sDate.toISOString().split('T')[0] === date;
      }).sort((a, b) => {
        const aDate = new Date(a.detected_at);
        const bDate = new Date(b.detected_at);
        if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) return 0;
        return aDate.getTime() - bDate.getTime();
      }) || [];
      
      const storyNumber = sameUserStories.findIndex(s => s.id === storyId) + 1;
      const fileExtension = story.story_type === 'video' ? 'mp4' : story.story_type === 'image' ? 'jpg' : 'manual';
      const fileName = `${story.instagram_username}-${date}-${storyNumber}.${fileExtension}`;

      // Dosyayı indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // URL'yi temizle
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message;
      const serverCode = err?.response?.data?.code;
      setError(serverMessage || 'Story indirilemedi');
      console.error('İndirme hatası:', serverCode || err?.response?.status, serverMessage || err);
    }
  };

  const handleDeleteDetectedStory = async (storyId: number) => {
    if (!id) return;
    const confirmDelete = window.confirm('Bu tespit edilen hikaye kaydı silinecek. Devam?');
    if (!confirmDelete) return;

    setDeletingStoryId(storyId);
    try {
      await axios.delete(`/api/stories/${storyId}`, { params: { campaign_id: id } });
      setExportFeedback({ open: true, message: 'Hikaye kaydı silindi', severity: 'success' });
      await fetchCampaign();
    } catch (err: any) {
      setExportFeedback({
        open: true,
        message: err?.response?.data?.message || 'Hikaye silinemedi',
        severity: 'error'
      });
    } finally {
      setDeletingStoryId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'inactive': return 'Pasif';
      case 'completed': return 'Tamamlandı';
      default: return status;
    }
  };

  const handleChangeStatus = async (newStatus: 'active' | 'inactive' | 'completed') => {
    try {
      const payload: { status: typeof newStatus; auto_check_enabled?: boolean } = {
        status: newStatus
      };
      // Tamamlanınca otomatik takip de kapanır
      if (newStatus === 'completed') {
        payload.auto_check_enabled = false;
      }
      await axios.put(`/api/campaigns/${id}`, payload);
      fetchCampaign();
      
      const statusText = getStatusText(newStatus);
      alert(`Kampanya durumu "${statusText}" olarak güncellendi!`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Durum güncellenemedi');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Alert severity="error">
        {error || 'Kampanya bulunamadı'}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: '100vw',
        width: '100%',
        overflowX: 'hidden',
        px: { xs: 1, sm: 2, md: 0 },
        '& .MuiCard-root, & .MuiPaper-root, & .MuiCardContent-root, & .MuiGrid-item': {
          minWidth: 0,
          maxWidth: '100%'
        },
        '& .MuiButton-root': {
          maxWidth: '100%',
          whiteSpace: 'normal'
        },
        '& .MuiChip-root': {
          maxWidth: '100%'
        },
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      }}
    >
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        mb={3}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ minWidth: 0, maxWidth: '100%' }}>
          <IconButton onClick={() => navigate('/campaigns')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ ...wrapAnywhereSx, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {campaign.title}
          </Typography>
          <Chip
            label={getStatusText(campaign.status)}
            color={getStatusColor(campaign.status) as any}
            sx={{ ml: { xs: 0, sm: 1 } }}
          />
        </Box>
        
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ShareIcon />}
            onClick={handleOpenShareDialog}
            sx={mobileButtonSx}
          >
            Rapor Paylaş
          </Button>
          {campaign.status !== 'active' && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleChangeStatus('active')}
              sx={mobileButtonSx}
            >
              Başlat
            </Button>
          )}
          {campaign.status === 'active' && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => handleChangeStatus('inactive')}
              sx={mobileButtonSx}
            >
              Durdur
            </Button>
          )}
          {campaign.status !== 'completed' && (
            <Button
              variant="contained"
              color="info"
              size="small"
              onClick={() => handleChangeStatus('completed')}
              sx={mobileButtonSx}
            >
              Sonlandır
            </Button>
          )}
        </Box>
      </Box>

      <Card sx={{ ...responsiveCardSx, mb: 3 }}>
        <CardContent sx={responsiveCardContentSx}>
          <Typography variant="h6" gutterBottom>
            Kampanya Bilgileri
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" sx={wrapAnywhereSx}>
                <strong>Açıklama:</strong> {campaign.description}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Başlangıç:</strong> {new Date(campaign.start_date).toLocaleDateString('tr-TR')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Bitiş:</strong> {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('tr-TR') : 'Belirsiz'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Influencer Sayısı:</strong> {campaign.influencers.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ ...responsiveCardSx, mb: 3 }}>
        <CardContent sx={responsiveCardContentSx}>
          <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={2} mb={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">
                Kampanya Takip Ayarları
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Takip türü:{' '}
                {(campaign.content_tracking_mode || 'story') === 'story'
                  ? 'Hikâye'
                  : (campaign.content_tracking_mode || 'story') === 'feed'
                    ? 'Gönderi ve Reels'
                    : 'Hikâye + Gönderi / Reels'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                // Geriye uyumluluk: Eski string[] formatını LinkTarget[] formatına çevir
                const normalizeLinks = (links: any): LinkTarget[] => {
                  if (!links || !Array.isArray(links)) return [];
                  return links.map(link => {
                    if (typeof link === 'string') {
                      return { url: link, matchType: 'contains' as 'contains' | 'exact' };
                    } else {
                      return link;
                    }
                  });
                };
                
                // Kampanya verilerini settings'e yükle
                setSettings({
                  target_hashtags: campaign.target_hashtags,
                  target_mentions: campaign.target_mentions,
                  target_links: normalizeLinks(campaign.target_links),
                  content_tracking_mode: (campaign.content_tracking_mode as ContentTrackingMode) || 'story',
                  feed_post_limit: Math.min(
                    50,
                    Math.max(1, Number(campaign.feed_post_limit) > 0 ? Number(campaign.feed_post_limit) : 12)
                  ),
                  target_caption_keywords: Array.isArray(campaign.target_caption_keywords)
                    ? [...campaign.target_caption_keywords]
                    : [],
                  auto_check_enabled: campaign.auto_check_enabled,
                  auto_check_interval: campaign.auto_check_interval,
                  auto_check_start_time: formatTimeForInput(campaign.auto_check_start_time),
                  auto_check_end_time: formatTimeForInput(campaign.auto_check_end_time),
                  check_only_undetected: campaign.check_only_undetected || false
                });
                // Interval'i görüntüleme formatına çevir
                const minutes = campaign.auto_check_interval;
                if (minutes >= 60 && minutes % 60 === 0) {
                  setIntervalValue(minutes / 60);
                  setIntervalUnit('hours');
                } else {
                  setIntervalValue(minutes);
                  setIntervalUnit('minutes');
                }
                setOpenSettingsDialog(true);
              }}
              sx={mobileButtonSx}
            >
              Ayarları Düzenle
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Hashtag'ler:
              </Typography>
              {campaign.target_hashtags.length > 0 ? (
                campaign.target_hashtags.map((tag, index) => (
                  <Chip key={index} label={`#${tag}`} size="small" sx={{ mr: 1, mb: 1 }} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Belirlenmemiş</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Mention'lar:
              </Typography>
              {campaign.target_mentions.length > 0 ? (
                campaign.target_mentions.map((mention, index) => (
                  <Chip key={index} label={`@${mention}`} size="small" sx={{ mr: 1, mb: 1 }} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Belirlenmemiş</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Linkler:
              </Typography>
              {campaign.target_links.length > 0 ? (
                campaign.target_links.map((link, index) => {
                  const linkUrl = typeof link === 'string' ? link : link.url;
                  const matchType = typeof link === 'string' ? '' : ` (${link.matchType === 'contains' ? 'İçeren' : 'Tam'})`;
                  return (
                    <Chip 
                      key={index} 
                      label={`${linkUrl}${matchType}`} 
                      size="small" 
                      sx={{
                        mr: 1,
                        mb: 1,
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }} 
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">Belirlenmemiş</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Caption ifadeleri:
              </Typography>
              {Array.isArray(campaign.target_caption_keywords) && campaign.target_caption_keywords.length > 0 ? (
                campaign.target_caption_keywords.map((phrase, index) => (
                  <Chip
                    key={index}
                    label={phrase}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{
                      mr: 1,
                      mb: 1,
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Belirlenmemiş</Typography>
              )}
            </Grid>
          </Grid>

          {/* Otomatik Kontrol Durumu */}
          {campaign.auto_check_enabled ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  🤖 Otomatik kontrol aktif
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {autoCheckCampaignCopy.scopeLine}
                </Typography>
                <Typography variant="caption" display="block">
                  Her {campaign.auto_check_interval >= 60 
                    ? `${campaign.auto_check_interval / 60} saat` 
                    : `${campaign.auto_check_interval} dakika`} bir otomatik kontrol yapılıyor
                </Typography>
                <Typography variant="caption" display="block">
                  Çalışma aralığı: {campaignScheduleInfo.range}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color={campaignScheduleInfo.withinWindow ? 'success.main' : 'warning.main'}
                >
                  {serverTime
                    ? `Sunucu saati ${serverTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} · `
                    : ''}
                  {campaignScheduleInfo.withinWindow ? 'Şu anda kontrol yapılabilir.' : 'Şu anda zaman aralığı dışında.'}
                </Typography>
                {lastAutoCheckDate && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Son kontrol: {lastAutoCheckDate.toLocaleString('tr-TR')}
                  </Typography>
                )}
              </Box>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                ℹ️ Otomatik kontrol kapalı - İsterseniz "Ayarları Düzenle" butonundan aktif edebilirsiniz
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card sx={responsiveCardSx}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={responsiveTabsSx}
          >
            <Tab label="Influencer'lar" />
            <Tab label="Tespit Edilen Hikayeler" />
            <Tab label="Gönderiler & Reels" />
            <Tab label="İstatistikler" />
            <Tab label="Manuel Kayıtlar" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <CardContent sx={responsiveCardContentSx}>
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexWrap="wrap" gap={2} mb={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6">
                    Kampanya Influencer'ları
                  </Typography>
                  {selectedInfluencers.length > 0 && (
                    <Typography variant="caption" color="primary">
                      {selectedInfluencers.length} kişi seçildi
                    </Typography>
                  )}
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenInfluencerDialog(true)}
                    sx={mobileButtonSx}
                  >
                    Influencer Ekle
                  </Button>
                  {selectedInfluencers.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleRemoveSelected}
                      sx={mobileButtonSx}
                    >
                      Seçilileri Sil ({selectedInfluencers.length})
                    </Button>
                  )}
                  {selectedInfluencers.length > 0 && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setBulkQuotaDialogOpen(true)}
                      sx={mobileButtonSx}
                    >
                      Seçililere kota uygula ({selectedInfluencers.length})
                    </Button>
                  )}
                {fetchingStories ? (
                  <Button
                    variant="contained"
                    color="error"
                          onClick={() => {
                            stopProcessingRef.current = true;
                            console.log('Durdur butonuna tıklandı');
                          }}
                    sx={mobileButtonSx}
                  >
                    Durdur
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowIcon />}
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      disabled={campaign.influencers.length === 0}
                      sx={mobileButtonSx}
                    >
                      {selectedInfluencers.length > 0 ? `Başlat (${selectedInfluencers.length})` : 'Başlat'}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={() => setAnchorEl(null)}
                    >
                      <MenuItem onClick={() => { setAnchorEl(null); handleUpdateProfiles(); }}>
                        <ListItemIcon>
                          <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          Profil Güncelle
                          <Typography variant="caption" display="block" color="text.secondary">
                            Sadece profil bilgileri
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                      {(campaign.content_tracking_mode || 'story') !== 'feed' && (
                        <MenuItem onClick={() => { setAnchorEl(null); handleFetchStories(); }}>
                          <ListItemIcon>
                            <MovieIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            Hikaye Kontrol
                            <Typography variant="caption" display="block" color="text.secondary">
                              Sadece story&apos;leri çek
                            </Typography>
                          </ListItemText>
                        </MenuItem>
                      )}
                      {(campaign.content_tracking_mode || 'story') !== 'story' && (
                        <MenuItem onClick={() => { setAnchorEl(null); handleFetchPosts(); }}>
                          <ListItemIcon>
                            <MovieIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            Gönderi / Reels Çek
                            <Typography variant="caption" display="block" color="text.secondary">
                              Akış ve Reels gönderilerini çek
                            </Typography>
                          </ListItemText>
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => { setAnchorEl(null); handleFullUpdate(); }}>
                        <ListItemIcon>
                          <AllInclusiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          Tam Güncelleme
                          <Typography variant="caption" display="block" color="text.secondary">
                            {(campaign.content_tracking_mode || 'story') === 'feed'
                              ? 'Profil + gönderiler'
                              : (campaign.content_tracking_mode || 'story') === 'both'
                                ? 'Profil + hikâye + gönderiler'
                                : 'Profil + hikâye'}
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => { setAnchorEl(null); handleCheckUndetectedOnly(); }}>
                        <ListItemIcon>
                          <SearchOffIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          Sadece Tespit Edilemeyenler
                          <Typography variant="caption" display="block" color="text.secondary">
                            {(campaign.content_tracking_mode || 'story') === 'feed'
                              ? 'Henüz eşleşen gönderisi olmayanlar'
                              : (campaign.content_tracking_mode || 'story') === 'both'
                                ? 'Hikâye ve gönderi tarafında eşleşmesi olmayanlar (sırayla)'
                                : 'Henüz eşleşen hikâyesi olmayanlar'}
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
                </Box>
              </Box>
              
              {/* Progress Bar */}
              {fetchingStories && batchProgress.total > 0 && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Batch {batchProgress.current}/{batchProgress.total} işleniyor
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      %{Math.round((batchProgress.current / batchProgress.total) * 100)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(batchProgress.current / batchProgress.total) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {processedInfluencers.length} influencer işlendi, {campaign.influencers.length - processedInfluencers.length - processingInfluencers.length} bekliyor
                  </Typography>
                </Box>
              )}
            </Box>

            <TableContainer component={Paper} sx={{ ...responsiveTableContainerSx, display: { xs: 'none', md: 'block' } }}>
              <Table
                sx={{
                  minWidth: 1200,
                  '& .MuiTableCell-root': {
                    px: { xs: 1, sm: 1.5, md: 2 }
                  }
                }}
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedInfluencers.length === campaign.influencers.length && campaign.influencers.length > 0}
                        indeterminate={selectedInfluencers.length > 0 && selectedInfluencers.length < campaign.influencers.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell width="50">#</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('name')}
                    >
                      <Box display="flex" alignItems="center">
                        Ad Soyad
                        {sortField === 'name' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('username')}
                    >
                      <Box display="flex" alignItems="center">
                        Kullanıcı Adı
                        {sortField === 'username' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ display: { xs: 'none', sm: 'table-cell' }, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('followers')}
                    >
                      <Box display="flex" alignItems="center">
                        Takipçi Sayısı
                        {sortField === 'followers' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Hesap Durumu</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('stories')}
                    >
                      <Box display="flex" alignItems="center">
                        Hikayeler
                        {sortField === 'stories' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    {(campaign.content_tracking_mode || 'story') !== 'story' && (
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => handleSort('posts')}
                      >
                        <Box display="flex" alignItems="center">
                          Post / Reels
                          {sortField === 'posts' && (
                            sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Hedef hikâye</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Hedef gönderi/Reels</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Görev</TableCell>
                    <TableCell 
                      sx={{ display: { xs: 'none', md: 'table-cell' }, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('lastStory')}
                    >
                      <Box display="flex" alignItems="center">
                        Son Paylaşım
                        {sortField === 'lastStory' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ display: { xs: 'none', lg: 'table-cell' }, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('added')}
                    >
                      <Box display="flex" alignItems="center">
                        Eklenme
                        {sortField === 'added' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ display: { xs: 'none', lg: 'table-cell' }, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('updated')}
                    >
                      <Box display="flex" alignItems="center">
                        Son Güncelleme
                        {sortField === 'updated' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedInfluencersMain.filter(inf => inf && inf.id).map((influencer, index) => {
                    // Bu influencer'a ait tespit edilen hikaye sayısı (kampanya genelinde)
                    const storyCount = influencer.detected_story_count || 0;
                    const postCount = influencer.detected_post_count || 0;
                    const showPostCol = (campaign.content_tracking_mode || 'story') !== 'story';

                    // Tarih alanlarını UTC kabul ederek yerel saate çevir
                    const lastStoryDate = parseUtcDateString(influencer.last_detected_at);
                    const addedDate = parseUtcDateString(influencer.added_at);
                    const lastUpdatedDate = parseUtcDateString(influencer.last_updated);

                    const isProcessing = processingInfluencers.includes(influencer.id);
                    const isProcessed = processedInfluencers.includes(influencer.id);
                    const hasError = errorInfluencers[influencer.id];
                    const pr = influencer.profile_reachable;
                    // NULL/undefined = henüz denenmedi — Number(null)===0 olur; sadece gerçek 0 ulaşılamadı demektir
                    const profileUnreachable =
                      pr !== null && pr !== undefined && Number(pr) === 0;

                    return (
                      <TableRow 
                        key={influencer.id} 
                        selected={selectedInfluencers.includes(influencer.id)}
                        sx={{
                          animation: isProcessing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                          backgroundColor: hasError || profileUnreachable
                            ? 'rgba(211, 47, 47, 0.1)'
                            : (isProcessed ? 'rgba(76, 175, 80, 0.08)' : 'inherit'),
                          position: 'relative'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedInfluencers.includes(influencer.id)}
                            onChange={() => handleSelectInfluencer(influencer.id)}
                            disabled={isProcessing}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2"
                            sx={{
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                              ...truncateCellTextSx
                            }}
                            title={cleanText(influencer.full_name) || influencer.instagram_username}
                          >
                            {cleanText(influencer.full_name) || influencer.instagram_username}
                          </Typography>
                          {hasError && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                              ⚠️ {hasError}
                            </Typography>
                          )}
                          {profileUnreachable && !hasError ? (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                              Profil verisi alınamadı
                            </Typography>
                          ) : isProcessed && !hasError ? (
                            <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                              ✅ Güncellendi
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {influencer.is_profile_detached ? (
                              <Typography variant="body2" color="text.secondary" sx={truncateCellTextSx}>
                                @{influencer.instagram_username}
                              </Typography>
                            ) : (
                              <a
                                href={
                                  influencer.instagram_url ||
                                  `https://www.instagram.com/${influencer.instagram_username}/`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none' }}
                              >
                                <Typography variant="body2" color="primary" sx={truncateCellTextSx}>
                                  @{influencer.instagram_username}
                                </Typography>
                              </a>
                            )}
                            {influencer.is_profile_detached ? (
                              <Chip size="small" label="Profil kaldırıldı" color="warning" variant="outlined" />
                            ) : null}
                            {profileUnreachable && (
                              <Tooltip title="Profil API üzerinden ulaşılamadı; kullanıcı adı değişmiş olabilir">
                                <WarningIcon color="error" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.primary">
                            {influencer.follower_count > 0 
                              ? influencer.follower_count.toLocaleString('tr-TR')
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Chip 
                            label={influencer.is_private ? 'Gizli' : 'Herkese Açık'} 
                            color={influencer.is_private ? 'warning' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={storyCount} 
                            color={storyCount > 0 ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        {showPostCol && (
                          <TableCell>
                            <Chip
                              label={postCount}
                              color={postCount > 0 ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        )}
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {(() => {
                            const mode = campaign.content_tracking_mode || 'story';
                            const qDisabled = mode === 'feed' || quotaSavingId === influencer.id;
                            const editing =
                              quotaEditCell?.id === influencer.id && quotaEditCell?.field === 's';
                            if (editing) {
                              return (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, max: 999 }}
                                  autoFocus
                                  value={quotaDraft[influencer.id]?.s ?? 0}
                                  onChange={(e) => {
                                    const v = Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0));
                                    setQuotaDraft((prev) => ({
                                      ...prev,
                                      [influencer.id]: { s: v, p: prev[influencer.id]?.p ?? 0 }
                                    }));
                                  }}
                                  onBlur={() => {
                                    void saveInfluencerQuota(influencer.id).finally(() =>
                                      setQuotaEditCell(null)
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      (e.target as HTMLInputElement).blur();
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setQuotaDraft((prev) => ({
                                        ...prev,
                                        [influencer.id]: {
                                          s: Math.min(999, Math.max(0, Number(influencer.task_quota_stories ?? 0))),
                                          p: Math.min(999, Math.max(0, Number(influencer.task_quota_posts ?? 0)))
                                        }
                                      }));
                                      setQuotaEditCell(null);
                                    }
                                  }}
                                  sx={{ width: 80 }}
                                />
                              );
                            }
                            return (
                              <Typography
                                component="span"
                                variant="body2"
                                onClick={() => {
                                  if (!qDisabled) setQuotaEditCell({ id: influencer.id, field: 's' });
                                }}
                                sx={{
                                  cursor: qDisabled ? 'default' : 'pointer',
                                  textDecoration: qDisabled ? 'none' : 'underline',
                                  color: qDisabled ? 'text.disabled' : 'primary.main',
                                  minWidth: 28,
                                  display: 'inline-block'
                                }}
                              >
                                {quotaDraft[influencer.id]?.s ?? 0}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {(() => {
                            const mode = campaign.content_tracking_mode || 'story';
                            const qDisabled = mode === 'story' || quotaSavingId === influencer.id;
                            const editing =
                              quotaEditCell?.id === influencer.id && quotaEditCell?.field === 'p';
                            if (editing) {
                              return (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, max: 999 }}
                                  autoFocus
                                  value={quotaDraft[influencer.id]?.p ?? 0}
                                  onChange={(e) => {
                                    const v = Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0));
                                    setQuotaDraft((prev) => ({
                                      ...prev,
                                      [influencer.id]: { s: prev[influencer.id]?.s ?? 0, p: v }
                                    }));
                                  }}
                                  onBlur={() => {
                                    void saveInfluencerQuota(influencer.id).finally(() =>
                                      setQuotaEditCell(null)
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      (e.target as HTMLInputElement).blur();
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setQuotaDraft((prev) => ({
                                        ...prev,
                                        [influencer.id]: {
                                          s: Math.min(999, Math.max(0, Number(influencer.task_quota_stories ?? 0))),
                                          p: Math.min(999, Math.max(0, Number(influencer.task_quota_posts ?? 0)))
                                        }
                                      }));
                                      setQuotaEditCell(null);
                                    }
                                  }}
                                  sx={{ width: 80 }}
                                />
                              );
                            }
                            return (
                              <Typography
                                component="span"
                                variant="body2"
                                onClick={() => {
                                  if (!qDisabled) setQuotaEditCell({ id: influencer.id, field: 'p' });
                                }}
                                sx={{
                                  cursor: qDisabled ? 'default' : 'pointer',
                                  textDecoration: qDisabled ? 'none' : 'underline',
                                  color: qDisabled ? 'text.disabled' : 'primary.main',
                                  minWidth: 28,
                                  display: 'inline-block'
                                }}
                              >
                                {quotaDraft[influencer.id]?.p ?? 0}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Chip
                            size="small"
                            label={taskQuotaStatusLabel(influencer.task_status)}
                            color={
                              influencer.task_status === 'completed'
                                ? 'success'
                                : influencer.task_status === 'in_progress'
                                  ? 'warning'
                                  : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {lastStoryDate 
                              ? lastStoryDate.toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2">
                            {addedDate ? addedDate.toLocaleDateString('tr-TR') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2">
                            {lastUpdatedDate 
                              ? lastUpdatedDate.toLocaleString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditProfile(influencer)}
                              title="Profili Düzenle"
                              disabled={influencer.is_profile_detached === true}
                            >
                              <EditIcon />
                            </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveInfluencer(influencer.id)}
                            title={
                              influencer.is_profile_detached
                                ? 'Listeden kaldır (tespitler kalır)'
                                : 'Kampanyadan Kaldır'
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Checkbox
                  checked={selectedInfluencers.length === campaign.influencers.length && campaign.influencers.length > 0}
                  indeterminate={selectedInfluencers.length > 0 && selectedInfluencers.length < campaign.influencers.length}
                  onChange={handleSelectAll}
                />
                <Typography variant="body2" color="text.secondary">
                  Tümünü seç ({selectedInfluencers.length}/{campaign.influencers.length})
                </Typography>
              </Box>
              {sortedInfluencersMain.filter(inf => inf && inf.id).map((influencer, index) => {
                const storyCount = influencer.detected_story_count || 0;
                const postCount = influencer.detected_post_count || 0;
                const showPostCol = (campaign.content_tracking_mode || 'story') !== 'story';
                const lastStoryDate = parseUtcDateString(influencer.last_detected_at);
                const isProcessing = processingInfluencers.includes(influencer.id);
                const hasError = errorInfluencers[influencer.id];
                const pr = influencer.profile_reachable;
                const profileUnreachable = pr !== null && pr !== undefined && Number(pr) === 0;

                return (
                  <Paper
                    key={influencer.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderColor: hasError || profileUnreachable ? 'error.light' : 'divider',
                      backgroundColor: selectedInfluencers.includes(influencer.id) ? 'action.selected' : 'background.paper'
                    }}
                  >
                    <Box display="flex" gap={1.5} alignItems="flex-start">
                      <Checkbox
                        checked={selectedInfluencers.includes(influencer.id)}
                        onChange={() => handleSelectInfluencer(influencer.id)}
                        disabled={isProcessing}
                        sx={{ mt: -0.5, p: 0.5 }}
                      />
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={700}>
                            #{index + 1} {cleanText(influencer.full_name) || influencer.instagram_username}
                          </Typography>
                          {isProcessing && <CircularProgress size={14} />}
                        </Box>
                        {influencer.is_profile_detached ? (
                          <Typography variant="body2" color="text.secondary">
                            @{influencer.instagram_username}
                          </Typography>
                        ) : (
                          <Typography
                            component="a"
                            href={influencer.instagram_url || `https://www.instagram.com/${influencer.instagram_username}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            color="primary"
                            sx={{ textDecoration: 'none', display: 'block', ...wrapAnywhereSx }}
                          >
                            @{influencer.instagram_username}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {influencer.follower_count > 0
                            ? `${influencer.follower_count.toLocaleString('tr-TR')} takipçi`
                            : 'Takipçi bilgisi yok'}
                        </Typography>
                        {hasError && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                            {hasError}
                          </Typography>
                        )}
                        {profileUnreachable && !hasError && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                            Profil verisi alınamadı
                          </Typography>
                        )}
                        <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                          <Chip label={`Hikâye: ${storyCount}`} size="small" color={storyCount > 0 ? 'success' : 'default'} />
                          {showPostCol && (
                            <Chip label={`Gönderi: ${postCount}`} size="small" color={postCount > 0 ? 'success' : 'default'} />
                          )}
                          <Chip
                            size="small"
                            label={influencer.is_private ? 'Gizli' : 'Açık'}
                            color={influencer.is_private ? 'warning' : 'success'}
                            variant="outlined"
                          />
                          {influencer.is_profile_detached && (
                            <Chip size="small" label="Profil kaldırıldı" color="warning" variant="outlined" />
                          )}
                          <Chip
                            size="small"
                            label={taskQuotaStatusLabel(influencer.task_status)}
                            color={
                              influencer.task_status === 'completed'
                                ? 'success'
                                : influencer.task_status === 'in_progress'
                                  ? 'warning'
                                  : 'default'
                            }
                          />
                        </Box>
                        {lastStoryDate && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                            Son paylaşım: {lastStoryDate.toLocaleDateString('tr-TR')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={0.5} mt={1.5}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditProfile(influencer)}
                        title="Profili Düzenle"
                        disabled={influencer.is_profile_detached === true}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveInfluencer(influencer.id)}
                        title={
                          influencer.is_profile_detached
                            ? 'Listeden kaldır (tespitler kalır)'
                            : 'Kampanyadan Kaldır'
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </CardContent>
        )}

        {tabValue === 1 && (
          <CardContent sx={responsiveCardContentSx}>
            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={2} mb={2}>
              <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">
                Tespit Edilen Hikayeler
              </Typography>
                <Typography variant="caption" color="text.secondary">
                  Toplam {campaign.recent_stories?.length || 0} hikaye tespit edildi • Son eklenenlere göre sıralanmış
                </Typography>
              </Box>
              {(campaign.recent_stories?.length || 0) > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadAllStories}
                  sx={mobileButtonSx}
                >
                  Tüm Story'leri İndir (ZIP)
                </Button>
              )}
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Hiç Paylaşım Yapmayan Influencerlar
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {nonSharingInfluencers.length > 0
                    ? `Toplam ${nonSharingInfluencers.length} influencer henüz paylaşım yapmadı.`
                    : 'Tüm influencerlar en az bir paylaşım yapmış görünüyor.'}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyNonSharingInfluencers}
                  disabled={!nonSharingInfluencers.length}
                >
                  Panoya Kopyala
                </Button>
              </Box>
            </Box>

            {(campaign.recent_stories?.length || 0) === 0 ? (
              <Alert severity="info">
                Henüz tespit edilen hikaye yok. Influencer'ları ekledikten sonra "Story'leri Çek" butonuna tıklayın veya otomatik kontrolü etkinleştirin.
              </Alert>
            ) : (
              <>
            <TableContainer component={Paper} sx={{ ...responsiveTableContainerSx, display: { xs: 'none', md: 'block' } }}>
              <Table sx={{ minWidth: 760 }} size="small">
                <TableHead>
                  <TableRow>
                        <TableCell>Tip</TableCell>
                    <TableCell>Influencer</TableCell>
                    <TableCell>Tespit Edilen Kelimeler</TableCell>
                    <TableCell>Tarih</TableCell>
                        <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaign.recent_stories.map((story) => {
                    const keywords = typeof story.detected_keywords === 'string' 
                      ? JSON.parse(story.detected_keywords || '[]') 
                      : (Array.isArray(story.detected_keywords) ? story.detected_keywords : []);
                    
                    const viewUrl = story.story_url || story.media_url || null;
                    const canDownload = story.story_type !== 'manual';
                    return (
                      <TableRow key={story.id}>
                            <TableCell>
                              {story.story_type === 'video' ? (
                                <MovieIcon fontSize="small" color="action" titleAccess="Video" />
                              ) : story.story_type === 'image' ? (
                                <AccountCircleIcon fontSize="small" color="action" titleAccess="Görsel" />
                              ) : (
                                <AllInclusiveIcon fontSize="small" color="warning" titleAccess="Manuel" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                @{story.instagram_username}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {cleanText(story.influencer_full_name) || '—'}
                              </Typography>
                            </TableCell>
                        <TableCell>
                          {keywords.map((keyword: string, index: number) => (
                                <Chip 
                                  key={index} 
                                  label={keyword} 
                                  size="small" 
                                  color={getManualKeywordChipColor(keyword)}
                                  variant="outlined"
                                  sx={{ mr: 0.5, mb: 0.5 }} 
                                />
                          ))}
                        </TableCell>
                        <TableCell>
                              <Typography variant="body2">
                                {new Date(story.detected_at).toLocaleDateString('tr-TR', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => viewUrl && window.open(viewUrl, '_blank')}
                            disabled={!viewUrl}
                          >
                            Görüntüle
                          </Button>
                          <Tooltip title="Instagram story sayfası (web)">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                component="a"
                                href={buildInstagramStoryWebUrl(story.instagram_username, story)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canDownload && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadSingleStory(story.id, story)}
                          >
                            İndir
                          </Button>
                          )}
                          <Tooltip title="Sil">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDetectedStory(story.id)}
                                disabled={deletingStoryId === story.id}
                              >
                                {deletingStoryId === story.id ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {campaign.recent_stories.map((story) => {
                const keywords = typeof story.detected_keywords === 'string'
                  ? JSON.parse(story.detected_keywords || '[]')
                  : (Array.isArray(story.detected_keywords) ? story.detected_keywords : []);
                const viewUrl = story.story_url || story.media_url || null;
                const canDownload = story.story_type !== 'manual';
                return (
                  <Paper key={story.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {story.story_type === 'video' ? (
                        <MovieIcon fontSize="small" color="action" />
                      ) : story.story_type === 'image' ? (
                        <AccountCircleIcon fontSize="small" color="action" />
                      ) : (
                        <AllInclusiveIcon fontSize="small" color="warning" />
                      )}
                      <Box minWidth={0}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          @{story.instagram_username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cleanText(story.influencer_full_name) || '—'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                      {keywords.map((keyword: string, kwIndex: number) => (
                        <Chip
                          key={kwIndex}
                          label={keyword}
                          size="small"
                          color={getManualKeywordChipColor(keyword)}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {new Date(story.detected_at).toLocaleString('tr-TR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => viewUrl && window.open(viewUrl, '_blank')}
                        disabled={!viewUrl}
                        sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
                      >
                        Görüntüle
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={buildInstagramStoryWebUrl(story.instagram_username, story)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
                      >
                        Instagram
                      </Button>
                      {canDownload && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadSingleStory(story.id, story)}
                          sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                        >
                          İndir
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDetectedStory(story.id)}
                        disabled={deletingStoryId === story.id}
                        sx={{ ml: 'auto' }}
                      >
                        {deletingStoryId === story.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
                
              </>
            )}
          </CardContent>
        )}

        {tabValue === 2 && (
          <CardContent sx={responsiveCardContentSx}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">
                  Tespit Edilen Gönderiler & Reels
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(campaign.content_tracking_mode || 'story') === 'story'
                    ? 'Bu kampanyada gönderi takibi kapalı (yalnızca hikâye). Ayarlardan "Gönderi ve Reels" veya "İkisi" seçebilirsiniz.'
                    : `Toplam ${campaign.recent_posts?.length || 0} kayıt • hedef kelime eşleşen paylaşımlar`}
                </Typography>
              </Box>
              {(campaign.content_tracking_mode || 'story') !== 'story' &&
                (campaign.recent_posts?.length || 0) > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllTrackedPosts}
                    sx={mobileButtonSx}
                  >
                    Tüm Gönderileri İndir (ZIP)
                  </Button>
                )}
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Gönderi paylaşmayan influencerlar
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(campaign.content_tracking_mode || 'story') === 'story'
                    ? 'Bu kampanyada gönderi takibi kapalı; liste anlamlı değil.'
                    : nonSharingPostInfluencers.length > 0
                      ? `Toplam ${nonSharingPostInfluencers.length} influencer henüz tespit edilen gönderiye sahip değil.`
                      : 'Tüm influencerların en az bir gönderi kaydı var görünüyor.'}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyNonSharingPostInfluencers}
                  disabled={(campaign.content_tracking_mode || 'story') === 'story' || !nonSharingPostInfluencers.length}
                >
                  Panoya Kopyala
                </Button>
              </Box>
            </Box>

            {(campaign.content_tracking_mode || 'story') === 'story' ? (
              <Alert severity="info">
                Gönderi ve Reels listesi bu kampanyada kullanılmıyor. Takip türünü kampanya ayarlarından değiştirin.
              </Alert>
            ) : (campaign.recent_posts?.length || 0) === 0 ? (
              <Alert severity="info">
                Henüz tespit edilen gönderi yok. Influencer sekmesinden &quot;Gönderi / Reels çek&quot; ile içerikleri çekin veya otomatik kontrolü açın.
              </Alert>
            ) : (
              <>
              <TableContainer component={Paper} sx={{ ...responsiveTableContainerSx, display: { xs: 'none', md: 'block' } }}>
                <Table sx={{ minWidth: 820 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={72}>Önizleme</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Tespit edilen</TableCell>
                      <TableCell>Paylaşım</TableCell>
                      <TableCell align="right">İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(campaign.recent_posts || []).map((post) => {
                      const keywords = typeof post.detected_keywords === 'string'
                        ? JSON.parse(post.detected_keywords || '[]')
                        : (Array.isArray(post.detected_keywords) ? post.detected_keywords : []);
                      return (
                        <TableRow key={post.id}>
                          <TableCell>
                            {post.thumbnail_url ? (
                              <Box
                                component="img"
                                src={post.thumbnail_url}
                                alt=""
                                sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }}
                              />
                            ) : (
                              <Box sx={{ width: 56, height: 56, bgcolor: 'action.hover', borderRadius: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                              <Chip
                                size="small"
                                label={post.is_reel ? 'Reels' : 'Gönderi'}
                                color={post.is_reel ? 'secondary' : 'default'}
                              />
                              {post.is_manual ||
                              String((post as { instagram_media_id?: string }).instagram_media_id || '').startsWith(
                                'manual-'
                              ) ? (
                                <Chip size="small" label="Manuel" color="info" variant="outlined" />
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              @{post.instagram_username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {cleanText(post.influencer_full_name) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {keywords.map((keyword: string, index: number) => (
                              <Chip
                                key={index}
                                label={keyword}
                                size="small"
                                color={getManualKeywordChipColor(keyword)}
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {post.posted_at
                                ? new Date(post.posted_at).toLocaleString('tr-TR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {trackedPostHasDownloadableFiles(post) ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => handleDownloadTrackedPostFile(post.id)}
                                >
                                  İndir
                                </Button>
                              ) : null}
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                href={post.permalink || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!post.permalink}
                              >
                                Instagram&apos;da aç
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {(campaign.recent_posts || []).map((post) => {
                  const keywords = typeof post.detected_keywords === 'string'
                    ? JSON.parse(post.detected_keywords || '[]')
                    : (Array.isArray(post.detected_keywords) ? post.detected_keywords : []);
                  return (
                    <Paper key={post.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                      <Box display="flex" gap={1.5} mb={1}>
                        {post.thumbnail_url ? (
                          <Box
                            component="img"
                            src={post.thumbnail_url}
                            alt=""
                            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                          />
                        ) : (
                          <Box sx={{ width: 56, height: 56, bgcolor: 'action.hover', borderRadius: 1, flexShrink: 0 }} />
                        )}
                        <Box minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            @{post.instagram_username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {cleanText(post.influencer_full_name) || '—'}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            <Chip size="small" label={post.is_reel ? 'Reels' : 'Gönderi'} color={post.is_reel ? 'secondary' : 'default'} />
                            {post.is_manual ||
                            String((post as { instagram_media_id?: string }).instagram_media_id || '').startsWith('manual-') ? (
                              <Chip size="small" label="Manuel" color="info" variant="outlined" />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                        {keywords.map((keyword: string, kwIndex: number) => (
                          <Chip
                            key={kwIndex}
                            label={keyword}
                            size="small"
                            color={getManualKeywordChipColor(keyword)}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {post.posted_at
                          ? new Date(post.posted_at).toLocaleString('tr-TR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '—'}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {trackedPostHasDownloadableFiles(post) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadTrackedPostFile(post.id)}
                            sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
                          >
                            İndir
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          component={Link}
                          href={post.permalink || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          disabled={!post.permalink}
                          sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
                        >
                          Instagram&apos;da aç
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
              </>
            )}
          </CardContent>
        )}

        {tabValue === 3 && (
          <CardContent sx={responsiveCardContentSx}>
            <Typography variant="h6" gutterBottom>
              Kampanya İstatistikleri
            </Typography>
            
            {campaign && (() => {
              const mode = campaign.content_tracking_mode || 'story';
              const totalInfluencers = campaign.influencers.length;
              const activeInfluencers = campaign.influencers.filter(
                inf => (inf.detected_story_count || 0) > 0
              ).length;
              const activePostInfluencers = campaign.influencers.filter(
                inf => (inf.detected_post_count || 0) > 0
              ).length;
              const fallbackStoryTotal = campaign.influencers.reduce(
                (sum, inf) => sum + (inf.detected_story_count || 0),
                0
              );
              const totalStories = campaign.recent_stories?.length || fallbackStoryTotal || 0;
              const fallbackPostTotal = campaign.influencers.reduce(
                (sum, inf) => sum + (inf.detected_post_count || 0),
                0
              );
              const totalPostsResolved =
                (campaign.tracked_posts_total ?? campaign.recent_posts?.length ?? 0) || fallbackPostTotal || 0;
              const participationRate = totalInfluencers > 0 ? (activeInfluencers / totalInfluencers * 100) : 0;
              const postParticipationRate =
                totalInfluencers > 0 ? (activePostInfluencers / totalInfluencers * 100) : 0;
              const successRate = participationRate;
              const postSuccessRate = postParticipationRate;
              const totalFollowersSum = campaign.influencers.reduce(
                (sum, inf) => sum + (Number(inf.follower_count) || 0),
                0
              );
              
              return (
              <Grid container spacing={3}>
                {/* Genel İstatistikler */}
                <Grid item xs={12} md={6} lg={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Toplam Influencer
                      </Typography>
                      <Typography variant="h4">
                        {totalInfluencers}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Toplam Takipçi (liste)
                      </Typography>
                      <Typography variant="h4" color="secondary.main">
                        {totalFollowersSum.toLocaleString('tr-TR')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Kampanyadaki influencerların takipçi toplamı
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {mode !== 'feed' && (
                  <>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Tespit Edilen Hikaye
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {totalStories}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Hikâye paylaşan
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {activeInfluencers}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            / {totalInfluencers} influencer
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Ortalama Hikaye/Kişi
                          </Typography>
                          <Typography variant="h4" color="info.main">
                            {totalInfluencers > 0 ? (totalStories / totalInfluencers).toFixed(1) : '0'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}

                {mode !== 'story' && (
                  <>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Tespit edilen gönderi / Reels
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {totalPostsResolved}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Gönderi paylaşan
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {activePostInfluencers}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            / {totalInfluencers} influencer
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Ortalama gönderi/kişi
                          </Typography>
                          <Typography variant="h4" color="info.main">
                            {totalInfluencers > 0 ? (totalPostsResolved / totalInfluencers).toFixed(1) : '0'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}

                {/* Kampanya Başarı Oranları */}
                {mode !== 'feed' && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom variant="body2">
                          Katılım (hikâye)
                        </Typography>
                        <Box display="flex" alignItems="baseline" mb={1}>
                          <Typography variant="h4" color={participationRate >= 75 ? 'success.main' : participationRate >= 50 ? 'warning.main' : 'error.main'}>
                            %{participationRate.toFixed(0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" ml={1}>
                            ({activeInfluencers}/{totalInfluencers} influencer)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={participationRate}
                          sx={{ height: 8, borderRadius: 1 }}
                          color={participationRate >= 75 ? 'success' : participationRate >= 50 ? 'warning' : 'error'}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {mode !== 'feed' && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom variant="body2">
                          Başarı skoru (hikâye)
                        </Typography>
                        <Box display="flex" alignItems="baseline" mb={1}>
                          <Typography variant="h4" color={successRate >= 70 ? 'success.main' : successRate >= 40 ? 'info.main' : 'warning.main'}>
                            %{successRate.toFixed(0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" ml={1}>
                            (ortalama {totalInfluencers > 0 ? (totalStories / totalInfluencers).toFixed(1) : '0'} hikâye/kişi)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={successRate}
                          sx={{ height: 8, borderRadius: 1 }}
                          color={successRate >= 70 ? 'success' : successRate >= 40 ? 'info' : 'warning'}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {mode !== 'story' && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom variant="body2">
                          Katılım (gönderi / Reels)
                        </Typography>
                        <Box display="flex" alignItems="baseline" mb={1}>
                          <Typography variant="h4" color={postParticipationRate >= 75 ? 'success.main' : postParticipationRate >= 50 ? 'warning.main' : 'error.main'}>
                            %{postParticipationRate.toFixed(0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" ml={1}>
                            ({activePostInfluencers}/{totalInfluencers} influencer)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={postParticipationRate}
                          sx={{ height: 8, borderRadius: 1 }}
                          color={postParticipationRate >= 75 ? 'success' : postParticipationRate >= 50 ? 'warning' : 'error'}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {mode !== 'story' && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom variant="body2">
                          Başarı skoru (gönderi)
                        </Typography>
                        <Box display="flex" alignItems="baseline" mb={1}>
                          <Typography variant="h4" color={postSuccessRate >= 70 ? 'success.main' : postSuccessRate >= 40 ? 'info.main' : 'warning.main'}>
                            %{postSuccessRate.toFixed(0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" ml={1}>
                            (ortalama {totalInfluencers > 0 ? (totalPostsResolved / totalInfluencers).toFixed(1) : '0'} gönderi/kişi)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={postSuccessRate}
                          sx={{ height: 8, borderRadius: 1 }}
                          color={postSuccessRate >= 70 ? 'success' : postSuccessRate >= 40 ? 'info' : 'warning'}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Influencer Başına Detaylı İstatistikler */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Influencer Başına Performans
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={responsiveTableContainerSx}>
                    <Table sx={{ minWidth: { xs: 560, sm: 760 } }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sıra</TableCell>
                          <TableCell>Ad Soyad</TableCell>
                          <TableCell>Kullanıcı Adı</TableCell>
                          <TableCell>Takipçi Sayısı</TableCell>
                          {mode !== 'feed' && <TableCell>Tespit Edilen Hikaye</TableCell>}
                          {mode !== 'story' && <TableCell>Post / Reels</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedInfluencersMain.map((influencer, index) => {
                          return (
                            <TableRow key={influencer.id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {cleanText(influencer.full_name) || influencer.instagram_username}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="primary">
                                  @{influencer.instagram_username}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {influencer.follower_count > 0 
                                    ? influencer.follower_count.toLocaleString('tr-TR')
                                    : '-'
                                  }
                                </Typography>
                              </TableCell>
                              {mode !== 'feed' && (
                                <TableCell>
                                  <Chip 
                                    label={influencer.detected_story_count || 0} 
                                    color={(influencer.detected_story_count || 0) > 0 ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              )}
                              {mode !== 'story' && (
                                <TableCell>
                                  <Chip
                                    label={influencer.detected_post_count || 0}
                                    color={(influencer.detected_post_count || 0) > 0 ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Kriterlere Göre Analiz */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Kriter Bazlı Analiz
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {campaign.target_hashtags.length > 0 && campaign.target_hashtags.map((hashtag, index) => {
                      const count = campaign.recent_stories.filter(story => {
                        try {
                          const keywords = Array.isArray(story.detected_keywords) 
                            ? story.detected_keywords 
                            : (typeof story.detected_keywords === 'string' 
                                ? JSON.parse(story.detected_keywords || '[]') 
                                : []);
                          // Case-insensitive ve Türkçe karakter duyarlı kontrol
                          const normalizedHashtag = hashtag.toLowerCase().trim();
                          return keywords.some((kw: string) => 
                            kw.toLowerCase().trim() === normalizedHashtag ||
                            kw.toLowerCase().trim() === `#${normalizedHashtag}` ||
                            kw.toLowerCase().trim().replace('#', '') === normalizedHashtag
                          );
                        } catch (e) {
                          return false;
                        }
                      }).length;
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`hashtag-${index}`}>
                          <Card variant="outlined" sx={{ height: '100%', minHeight: 100 }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Hashtag Kullanımı
                              </Typography>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                #{hashtag}
                              </Typography>
                              <Typography variant="h5" color="success.main" sx={{ mt: 1 }}>
                                {count}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                hikaye
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}

                    {campaign.target_mentions.length > 0 && campaign.target_mentions.map((mention, index) => {
                      const count = campaign.recent_stories.filter(story => {
                        try {
                          const keywords = Array.isArray(story.detected_keywords) 
                            ? story.detected_keywords 
                            : (typeof story.detected_keywords === 'string' 
                                ? JSON.parse(story.detected_keywords || '[]') 
                                : []);
                          // Case-insensitive ve Türkçe karakter duyarlı kontrol
                          const normalizedMention = mention.toLowerCase().trim();
                          return keywords.some((kw: string) => 
                            kw.toLowerCase().trim() === normalizedMention ||
                            kw.toLowerCase().trim() === `@${normalizedMention}` ||
                            kw.toLowerCase().trim().replace('@', '') === normalizedMention
                          );
                        } catch (e) {
                          return false;
                        }
                      }).length;
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`mention-${index}`}>
                          <Card variant="outlined" sx={{ height: '100%', minHeight: 100 }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Mention Kullanımı
                              </Typography>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                @{mention}
                              </Typography>
                              <Typography variant="h5" color="success.main" sx={{ mt: 1 }}>
                                {count}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                hikaye
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}

                    {campaign.target_links.length > 0 && campaign.target_links.map((link, index) => {
                      const linkUrl = typeof link === 'string' ? link : link.url;
                      const matchType = typeof link === 'string' ? '' : ` (${link.matchType === 'contains' ? 'İçeren' : 'Tam'})`;
                      
                      const count = campaign.recent_stories.filter(story => {
                        try {
                          const keywords = Array.isArray(story.detected_keywords) 
                            ? story.detected_keywords 
                            : (typeof story.detected_keywords === 'string' 
                                ? JSON.parse(story.detected_keywords || '[]') 
                                : []);
                          return keywords.some((keyword: string) => keyword.includes(linkUrl));
                        } catch (e) {
                          return false;
                        }
                      }).length;
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`link-${index}`}>
                          <Card variant="outlined" sx={{ height: '100%', minHeight: 100 }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Link Kullanımı{matchType}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="primary"
                                fontWeight="bold"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {linkUrl}
                              </Typography>
                              <Typography variant="h5" color="success.main" sx={{ mt: 1 }}>
                                {count}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                hikaye
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>

                {/* Tarih Bazlı Analiz */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Tarih Bazlı Hikaye Analizi
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={responsiveTableContainerSx}>
                    <Table sx={{ minWidth: { xs: 640, sm: 820 } }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tarih</TableCell>
                          <TableCell>Gün</TableCell>
                          <TableCell align="center">Toplam Hikaye</TableCell>
                          <TableCell align="center">Aktif Influencer</TableCell>
                          <TableCell align="center">Hashtag İçeren</TableCell>
                          <TableCell align="center">Mention İçeren</TableCell>
                          <TableCell align="center">Link İçeren</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          // Story'leri tarihe göre grupla
                          const storyByDate: { [key: string]: any[] } = {};
                          campaign.recent_stories.forEach(story => {
                            const timestamp = story.story_timestamp || story.detected_at;
                            if (!timestamp) return; // Tarih yoksa atla
                            
                            const storyDate = new Date(timestamp);
                            // Geçersiz tarih kontrolü
                            if (isNaN(storyDate.getTime())) return;
                            
                            const dateKey = storyDate.toISOString().split('T')[0]; // YYYY-MM-DD
                            if (!storyByDate[dateKey]) {
                              storyByDate[dateKey] = [];
                            }
                            storyByDate[dateKey].push(story);
                          });

                          // Tarihleri sırala (en yeni en üstte)
                          const sortedDates = Object.keys(storyByDate).sort((a, b) => 
                            new Date(b).getTime() - new Date(a).getTime()
                          );

                          const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

                          return sortedDates.map(dateKey => {
                            const stories = storyByDate[dateKey];
                            const date = new Date(dateKey);
                            const dayName = dayNames[date.getDay()];
                            
                            // Benzersiz influencer sayısı
                            const uniqueInfluencers = new Set(stories.map(s => s.instagram_username)).size;
                            
                            // Hashtag içeren story sayısı
                            const hashtagCount = stories.filter(story => {
                              if (!campaign.target_hashtags.length) return false;
                              try {
                                const keywords = Array.isArray(story.detected_keywords) 
                                  ? story.detected_keywords 
                                  : (typeof story.detected_keywords === 'string' 
                                      ? JSON.parse(story.detected_keywords || '[]') 
                                      : []);
                                return campaign.target_hashtags.some(hashtag => {
                                  const normalizedHashtag = hashtag.toLowerCase().trim();
                                  return keywords.some((kw: string) => 
                                    kw.toLowerCase().trim() === normalizedHashtag ||
                                    kw.toLowerCase().trim() === `#${normalizedHashtag}` ||
                                    kw.toLowerCase().trim().replace('#', '') === normalizedHashtag
                                  );
                                });
                              } catch (e) {
                                return false;
                              }
                            }).length;

                            // Mention içeren story sayısı
                            const mentionCount = stories.filter(story => {
                              if (!campaign.target_mentions.length) return false;
                              try {
                                const keywords = Array.isArray(story.detected_keywords) 
                                  ? story.detected_keywords 
                                  : (typeof story.detected_keywords === 'string' 
                                      ? JSON.parse(story.detected_keywords || '[]') 
                                      : []);
                                return campaign.target_mentions.some(mention => {
                                  const normalizedMention = mention.toLowerCase().trim();
                                  return keywords.some((kw: string) => 
                                    kw.toLowerCase().trim() === normalizedMention ||
                                    kw.toLowerCase().trim() === `@${normalizedMention}` ||
                                    kw.toLowerCase().trim().replace('@', '') === normalizedMention
                                  );
                                });
                              } catch (e) {
                                return false;
                              }
                            }).length;

                            // Link içeren story sayısı
                            const linkCount = stories.filter(story => {
                              if (!campaign.target_links.length) return false;
                              try {
                                const keywords = Array.isArray(story.detected_keywords) 
                                  ? story.detected_keywords 
                                  : (typeof story.detected_keywords === 'string' 
                                      ? JSON.parse(story.detected_keywords || '[]') 
                                      : []);
                                return campaign.target_links.some(link => {
                                  const linkUrl = typeof link === 'string' ? link : link.url;
                                  return keywords.some((keyword: string) => keyword.includes(linkUrl));
                                });
                              } catch (e) {
                                return false;
                              }
                            }).length;

                            return (
                              <TableRow key={dateKey}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">
                                    {date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {dayName}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={stories.length} 
                                    color="primary" 
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {uniqueInfluencers}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color={hashtagCount > 0 ? 'success.main' : 'text.secondary'}>
                                    {hashtagCount}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color={mentionCount > 0 ? 'success.main' : 'text.secondary'}>
                                    {mentionCount}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color={linkCount > 0 ? 'success.main' : 'text.secondary'}>
                                    {linkCount}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
              );
            })()}
          </CardContent>
        )}

        {tabValue === 4 && (
          <CardContent sx={responsiveCardContentSx}>
            <Typography variant="h6" gutterBottom>
              Manuel Kayıt Ekle
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kampanya dışında kalan paylaşımları buradan manuel olarak ekleyebilir ve raporlara dahil edebilirsiniz.
            </Typography>

              <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Kayıt türü</FormLabel>
              <RadioGroup
                row
                sx={{ gap: { xs: 0, sm: 1 } }}
                value={manualRecordKind}
                onChange={(e) => {
                  setManualRecordKind(e.target.value as 'story' | 'post');
                  setManualFormError(null);
                  if (e.target.value === 'story') {
                    setManualStoryEntryMode('link');
                  }
                }}
              >
                <FormControlLabel value="story" control={<Radio />} label="Hikâye" />
                <FormControlLabel value="post" control={<Radio />} label="Gönderi / Reels" />
              </RadioGroup>
            </FormControl>

            {manualRecordKind === 'story' && manualFormError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {manualFormError}
              </Alert>
            )}

            {manualRecordKind === 'story' && (
            <Box
              component="form"
              onSubmit={handleManualSubmit}
              sx={{ mt: manualFormError ? 2 : 3 }}
            >
              {!editingManualEntryId && (
                <Tabs
                  value={manualStoryEntryMode}
                  onChange={(_, v) => {
                    setManualStoryEntryMode(v as 'link' | 'upload');
                    setManualStoryPreview(null);
                    setManualFormError(null);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ ...responsiveTabsSx, mb: 2 }}
                >
                  <Tab label="Linkten getir" value="link" />
                  <Tab label="Dosya yükle (yedek)" value="upload" />
                </Tabs>
              )}
              <Grid container spacing={2}>
                {!editingManualEntryId && manualStoryEntryMode === 'link' && (
                  <Grid item xs={12}>
                    <Tabs
                      value={manualStoryLinkMode}
                      onChange={(_, v) => {
                        setManualStoryLinkMode(v as 'single' | 'bulk');
                        setManualStoryPreview(null);
                        setManualStoryBulkResult(null);
                        setManualFormError(null);
                      }}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{ mb: 1 }}
                    >
                      <Tab label="Tek link" value="single" />
                      <Tab label="Toplu link" value="bulk" />
                    </Tabs>
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'single' && (
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={9}>
                        <TextField
                          fullWidth
                          label="Instagram hikaye linki"
                          placeholder="https://www.instagram.com/stories/kullanici/..."
                          value={manualStoryUrl}
                          onChange={(event) => {
                            setManualStoryUrl(event.target.value);
                            setManualStoryPreview(null);
                          }}
                          helperText="Link girerseniz influencer kampanyadan otomatik eşleştirilmeye çalışılır."
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleManualStoryPreview}
                          disabled={manualStoryPreviewLoading || !manualStoryUrl.trim()}
                          fullWidth
                          sx={{ minHeight: 56 }}
                        >
                          {manualStoryPreviewLoading ? 'Getiriliyor…' : 'Getir (önizleme)'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'bulk' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      maxRows={14}
                      label="Instagram hikaye linkleri"
                      placeholder={'Her satıra bir story linki\nhttps://www.instagram.com/stories/kullanici/123...\nhttps://www.instagram.com/stories/kullanici/456...'}
                      value={manualStoryBulkUrls}
                      onChange={(event) => {
                        setManualStoryBulkUrls(event.target.value);
                        setManualStoryBulkResult(null);
                      }}
                      helperText="Her satıra bir link yapıştırın. Boş satırlar yok sayılır."
                    />
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'bulk' && (
                  <Grid item xs={12}>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleManualStoryBulk}
                      disabled={manualStoryBulkLoading || !manualStoryBulkUrls.trim()}
                      fullWidth
                    >
                      {manualStoryBulkLoading ? 'İşleniyor…' : 'Toplu kontrol'}
                    </Button>
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'single' && manualStoryPreview && (
                  <Grid item xs={12}>
                    <Alert severity={manualStoryPreview.skipped ? 'info' : manualStoryPreview.owner_username_match ? 'success' : 'warning'}>
                      {manualStoryPreview.message ||
                        (manualStoryPreview.owner_username_match
                          ? 'Hikaye linki bulundu.'
                          : 'Uyarı: Linkteki kullanıcı ile seçilen influencer eşleşmeyebilir.')}
                      {manualStoryPreview.owner_username && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Linkteki kullanıcı: @{manualStoryPreview.owner_username}
                        </Typography>
                      )}
                      {manualStoryPreview.suggested_influencer_username && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Eşleşen influencer: @{manualStoryPreview.suggested_influencer_username}
                        </Typography>
                      )}
                      {manualStoryPreview.download_status && !manualStoryPreview.skipped && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Medya:{' '}
                          {manualStoryPreview.download_status === 'downloaded'
                            ? 'İndirildi'
                            : manualStoryPreview.download_status === 'failed'
                              ? `İndirilemedi${manualStoryPreview.download_error ? ` — ${manualStoryPreview.download_error}` : ''}`
                              : 'Atlandı'}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'bulk' && manualStoryBulkProgress && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      İşleniyor: {manualStoryBulkProgress.current}/{manualStoryBulkProgress.total}
                    </Typography>
                    <LinearProgress
                      variant={manualStoryBulkLoading ? 'indeterminate' : 'determinate'}
                      value={
                        manualStoryBulkProgress.total > 0
                          ? (manualStoryBulkProgress.current / manualStoryBulkProgress.total) * 100
                          : 0
                      }
                    />
                  </Grid>
                )}
                {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'bulk' && manualStoryBulkResult && (
                  <Grid item xs={12}>
                    <Alert severity={manualStoryBulkResult.failed.length > 0 ? 'info' : 'success'} sx={{ mb: 1 }}>
                      {manualStoryBulkResult.processed} link işlendi — {manualStoryBulkResult.added} eklendi,{' '}
                      {manualStoryBulkResult.updated} güncellendi, {manualStoryBulkResult.skipped} atlandı,{' '}
                      {manualStoryBulkResult.failed.length} hata
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Link</TableCell>
                            <TableCell>Durum</TableCell>
                            <TableCell>Açıklama</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {manualStoryBulkResult.results.map((row, idx) => (
                            <TableRow key={`${row.url}-${idx}`}>
                              <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                                <Typography variant="caption">{row.url}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    row.status === 'added'
                                      ? 'Eklendi'
                                      : row.status === 'updated'
                                        ? 'Güncellendi'
                                        : row.status === 'skipped'
                                          ? 'Atlandı'
                                          : 'Hata'
                                  }
                                  color={
                                    row.status === 'added' || row.status === 'updated'
                                      ? 'success'
                                      : row.status === 'skipped'
                                        ? 'default'
                                        : 'error'
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {row.influencer_username ? `@${row.influencer_username} — ` : ''}
                                  {row.reason || '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
                {(editingManualEntryId || manualStoryEntryMode === 'upload' || manualStoryLinkMode === 'single') && (
                <>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required={
                      Boolean(editingManualEntryId) ||
                      manualStoryEntryMode === 'upload' ||
                      (manualStoryEntryMode === 'link' && manualStoryLinkMode === 'single' && !manualStoryUrl.trim())
                    }
                  >
                    <InputLabel>Influencer</InputLabel>
                    <Select
                      label="Influencer"
                      value={manualForm.influencer_id === '' ? '' : String(manualForm.influencer_id || '')}
                      onChange={(event) => {
                        const value = event.target.value;
                        setManualForm(prev => ({
                          ...prev,
                          influencer_id: value === '' ? '' : Number(value)
                        }));
                        setManualStoryPreview(null);
                      }}
                    >
                      <MenuItem value="">
                        <em>Seçiniz</em>
                      </MenuItem>
                      {(campaign?.influencers || []).filter(inf => inf && inf.id).map((inf) => (
                        <MenuItem key={inf.id} value={String(inf.id)}>
                          {cleanText(inf.full_name) || `@${inf.instagram_username || ''}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'single' && manualStoryUrl.trim() ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                        Boş bırakılırsa linkten otomatik eşleştirilir.
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="datetime-local"
                    label="Paylaşım Tarihi"
                    value={manualForm.story_timestamp}
                    onChange={(event) => setManualForm(prev => ({ ...prev, story_timestamp: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>İçerik Türü</InputLabel>
                    <Select
                      label="İçerik Türü"
                      value={manualForm.story_type}
                      onChange={(event) => setManualForm(prev => ({ ...prev, story_type: event.target.value as ManualMediaType }))}
                    >
                      <MenuItem value="manual">Manuel</MenuItem>
                      <MenuItem value="image">Görsel</MenuItem>
                      <MenuItem value="video">Video</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {(editingManualEntryId || manualStoryEntryMode === 'upload') && (
                <Grid item xs={12} md={6}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    {manualForm.file ? manualForm.file.name : 'Dosya Seç'}
                    <input
                      key={manualFileInputKey}
                      type="file"
                      hidden
                      accept="image/*,video/*"
                      onChange={handleManualFileChange}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Desteklenen formatlar: Görsel veya video dosyaları.
                    {manualStoryEntryMode === 'upload'
                      ? ' Dosya yükleme modunda medya zorunludur.'
                      : ' Düzenlemede mevcut dosyayı değiştirebilirsiniz.'}
                  </Typography>
                  {manualForm.media_url && !manualForm.file && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Mevcut dosya: {' '}
                      <Link href={manualForm.media_url} target="_blank" rel="noopener noreferrer">
                        Görüntüle
                      </Link>
                    </Typography>
                  )}
                  {manualForm.file && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Seçilen dosya: {manualForm.file.name}
                    </Typography>
                  )}
                </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Hashtagler</InputLabel>
                    <Select
                      multiple
                      label="Hashtagler"
                      value={manualForm.selectedHashtags}
                      onChange={(event) => {
                        const value = event.target.value;
                        setManualForm(prev => ({
                          ...prev,
                          selectedHashtags: typeof value === 'string' ? value.split(',') : value
                        }));
                      }}
                      renderValue={(selected) => (selected as string[]).map((tag) => `#${tag}`).join(', ')}
                    >
                      {(campaign?.target_hashtags || []).length > 0 ? (
                        (campaign?.target_hashtags || []).map((tag) => {
                          const plainTag = typeof tag === 'string' ? tag.replace(/^#/, '') : String(tag);
                          return (
                            <MenuItem key={plainTag} value={plainTag}>
                              #{plainTag}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled>Kriter bulunmuyor</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Mentionlar</InputLabel>
                    <Select
                      multiple
                      label="Mentionlar"
                      value={manualForm.selectedMentions}
                      onChange={(event) => {
                        const value = event.target.value;
                        setManualForm(prev => ({
                          ...prev,
                          selectedMentions: typeof value === 'string' ? value.split(',') : value
                        }));
                      }}
                      renderValue={(selected) => (selected as string[]).map((mention) => `@${mention}`).join(', ')}
                    >
                      {(campaign?.target_mentions || []).length > 0 ? (
                        (campaign?.target_mentions || []).map((mention) => {
                          const plainMention = typeof mention === 'string' ? mention.replace(/^@/, '') : String(mention);
                          return (
                            <MenuItem key={plainMention} value={plainMention}>
                              @{plainMention}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled>Kriter bulunmuyor</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Linkler</InputLabel>
                    <Select
                      multiple
                      label="Linkler"
                      value={manualForm.selectedLinks}
                      onChange={(event) => {
                        const value = event.target.value;
                        setManualForm(prev => ({
                          ...prev,
                          selectedLinks: typeof value === 'string' ? value.split(',') : value
                        }));
                      }}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {(campaign?.target_links || []).length > 0 ? (
                        (campaign?.target_links || []).map((linkItem) => {
                          const linkValue = typeof linkItem === 'string' ? linkItem : (linkItem?.url || '');
                          if (!linkValue) return null;
                          return (
                            <MenuItem key={linkValue} value={linkValue}>
                              {linkValue}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled>Kriter bulunmuyor</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                </>
                )}
              </Grid>

              {(editingManualEntryId || manualStoryEntryMode === 'upload' || manualStoryLinkMode === 'single') && (
              <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                  <Button type="submit" variant="contained" color="primary" disabled={manualSubmitting} sx={mobileButtonSx}>
                  {editingManualEntryId ? 'Manuel Kaydı Güncelle' : 'Manuel Kayıt Oluştur'}
                </Button>
                <Button type="button" variant="outlined" onClick={resetManualForm} disabled={manualSubmitting} sx={mobileButtonSx}>
                  {editingManualEntryId ? 'İptal' : 'Temizle'}
                </Button>
              </Box>
              )}
              {!editingManualEntryId && manualStoryEntryMode === 'link' && manualStoryLinkMode === 'bulk' && (
              <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                <Button type="button" variant="outlined" onClick={resetManualForm} disabled={manualStoryBulkLoading} sx={mobileButtonSx}>
                  Temizle
                </Button>
              </Box>
              )}
            </Box>
            )}

            {manualRecordKind === 'post' && (
              <Box component="form" onSubmit={handleManualPostSubmit} sx={{ mt: 3 }}>
                {!editingManualPostId && (
                  <Tabs
                    value={manualPostEntryMode}
                    onChange={(_, v) => {
                      setManualPostEntryMode(v as 'link' | 'upload');
                      setManualPostPreview(null);
                      setManualPostConfirmAssign(false);
                      setManualPostBulkResult(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ ...responsiveTabsSx, mb: 2 }}
                  >
                    <Tab label="Linkten getir" value="link" />
                    <Tab label="Dosya yükle (yedek)" value="upload" />
                  </Tabs>
                )}
                <Grid container spacing={2}>
                  {!editingManualPostId && manualPostEntryMode === 'link' && (
                    <Grid item xs={12}>
                      <Tabs
                        value={manualPostLinkMode}
                        onChange={(_, v) => {
                          setManualPostLinkMode(v as 'single' | 'bulk');
                          setManualPostPreview(null);
                          setManualPostBulkResult(null);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ mb: 1 }}
                      >
                        <Tab label="Tek link" value="single" />
                        <Tab label="Toplu link" value="bulk" />
                      </Tabs>
                    </Grid>
                  )}
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'single' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Instagram gönderi veya Reels linki"
                        placeholder="https://www.instagram.com/p/... veya /reel/..."
                        value={manualPostUrl}
                        onChange={(e) => {
                          setManualPostUrl(e.target.value);
                          setManualPostPreview(null);
                          setManualPostConfirmAssign(false);
                        }}
                        helperText="Link girerseniz influencer kampanyadan otomatik eşleştirilmeye çalışılır."
                      />
                    </Grid>
                  )}
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'bulk' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={14}
                        label="Instagram gönderi veya Reels linkleri"
                        placeholder={'Her satıra bir gönderi linki\nhttps://www.instagram.com/p/...\nhttps://www.instagram.com/reel/...'}
                        value={manualPostBulkUrls}
                        onChange={(event) => {
                          setManualPostBulkUrls(event.target.value);
                          setManualPostBulkResult(null);
                        }}
                        helperText="Her satıra bir link yapıştırın. Boş satırlar yok sayılır. Influencer kampanyadan otomatik eşleştirilir."
                      />
                    </Grid>
                  )}
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'bulk' && (
                    <Grid item xs={12}>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleManualPostBulk}
                        disabled={manualPostBulkLoading || !manualPostBulkUrls.trim()}
                        fullWidth
                      >
                        {manualPostBulkLoading ? 'İşleniyor…' : 'Toplu kontrol'}
                      </Button>
                    </Grid>
                  )}
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'bulk' && manualPostBulkProgress && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        İşleniyor: {manualPostBulkProgress.current}/{manualPostBulkProgress.total}
                      </Typography>
                      <LinearProgress
                        variant={manualPostBulkLoading ? 'indeterminate' : 'determinate'}
                        value={
                          manualPostBulkProgress.total > 0
                            ? (manualPostBulkProgress.current / manualPostBulkProgress.total) * 100
                            : 0
                        }
                      />
                    </Grid>
                  )}
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'bulk' && manualPostBulkResult && (
                    <Grid item xs={12}>
                      <Alert severity={manualPostBulkResult.failed.length > 0 ? 'info' : 'success'} sx={{ mb: 1 }}>
                        {manualPostBulkResult.processed} link işlendi — {manualPostBulkResult.added} eklendi,{' '}
                        {manualPostBulkResult.updated} güncellendi, {manualPostBulkResult.skipped} atlandı,{' '}
                        {manualPostBulkResult.failed.length} hata
                      </Alert>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Link</TableCell>
                              <TableCell>Durum</TableCell>
                              <TableCell>Açıklama</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {manualPostBulkResult.results.map((row, idx) => (
                              <TableRow key={`${row.url}-${idx}`}>
                                <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                                  <Typography variant="caption">{row.url}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={
                                      row.status === 'added'
                                        ? 'Eklendi'
                                        : row.status === 'updated'
                                          ? 'Güncellendi'
                                          : row.status === 'skipped'
                                            ? 'Atlandı'
                                            : 'Hata'
                                    }
                                    color={
                                      row.status === 'added' || row.status === 'updated'
                                        ? 'success'
                                        : row.status === 'skipped'
                                          ? 'default'
                                          : 'error'
                                    }
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.influencer_username ? `@${row.influencer_username} — ` : ''}
                                    {row.reason || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}
                  {(editingManualPostId || manualPostEntryMode === 'upload' || manualPostLinkMode === 'single') && (
                  <>
                  {!editingManualPostId && manualPostEntryMode === 'upload' && (
                    <>
                      <Grid item xs={12}>
                        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={manualPostSubmitting}>
                          Dosya seç (görsel veya video, birden fazla)
                          <input
                            key={manualPostFileInputKey}
                            type="file"
                            name="manual-post-files"
                            hidden
                            multiple
                            accept="image/*,video/*"
                            onChange={(e) => {
                              const list = e.target.files ? Array.from(e.target.files) : [];
                              setManualPostFiles(list);
                            }}
                          />
                        </Button>
                        {manualPostFiles.length > 0 ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {manualPostFiles.length} dosya seçildi
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            API veya gizli profil nedeniyle link çalışmıyorsa dosyaları buradan ekleyin.
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Kaynak link (isteğe bağlı)"
                          placeholder="Instagram adresi metin olarak saklanır"
                          value={manualPostSourceUrl}
                          onChange={(e) => setManualPostSourceUrl(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Not / açıklama (isteğe bağlı)"
                          value={manualPostNote}
                          onChange={(e) => setManualPostNote(e.target.value)}
                          multiline
                          minRows={2}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Paylaşım tarihi (isteğe bağlı)"
                          type="datetime-local"
                          value={manualPostPostedAt}
                          onChange={(e) => setManualPostPostedAt(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}
                  {editingManualPostId && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Instagram gönderi veya Reels linki"
                        value={manualPostUrl}
                        disabled
                        helperText="Düzenlemede link değişmez; iptal edip yeniden ekleyin."
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required={manualPostEntryMode === 'upload' || Boolean(editingManualPostId)}>
                      <InputLabel>Influencer</InputLabel>
                      <Select
                        label="Influencer"
                        value={manualPostInfluencerId === '' ? '' : String(manualPostInfluencerId)}
                        onChange={(event) => {
                          const value = event.target.value;
                          setManualPostInfluencerId(value === '' ? '' : Number(value));
                          setManualPostPreview(null);
                          setManualPostConfirmAssign(false);
                        }}
                      >
                        <MenuItem value="">
                          <em>Seçiniz</em>
                        </MenuItem>
                        {(campaign?.influencers || []).filter((inf) => inf && inf.id).map((inf) => (
                          <MenuItem key={inf.id} value={String(inf.id)}>
                            {cleanText(inf.full_name) || `@${inf.instagram_username || ''}`}
                          </MenuItem>
                        ))}
                      </Select>
                      {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'single' ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                          Boş bırakılırsa linkten otomatik eşleştirilir.
                        </Typography>
                      ) : null}
                    </FormControl>
                  </Grid>
                  {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'single' && (
                    <Grid item xs={12} md={4} display="flex" alignItems="flex-end">
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleManualPostPreview}
                        disabled={manualPostPreviewLoading || !manualPostUrl.trim()}
                        fullWidth
                      >
                        {manualPostPreviewLoading ? 'Getiriliyor…' : 'Getir (önizleme)'}
                      </Button>
                    </Grid>
                  )}
                  </>
                  )}
                </Grid>
                {manualPostEntryMode === 'link' && manualPostLinkMode === 'single' && manualPostPreview && (
                  <Alert
                    severity={manualPostPreview.skipped ? 'info' : manualPostPreview.owner_username_match ? 'success' : 'warning'}
                    sx={{ mt: 2 }}
                  >
                    {manualPostPreview.message ||
                      (manualPostPreview.owner_username_match
                        ? 'Gönderi bulundu.'
                        : 'Uyarı: API’deki içerik sahibi ile seçilen influencer eşleşmeyebilir.')}
                    {manualPostPreview.suggested_influencer_username && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Eşleşen influencer: @{manualPostPreview.suggested_influencer_username}
                      </Typography>
                    )}
                    {manualPostPreview.caption_preview && (
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {manualPostPreview.caption_preview.slice(0, 400)}
                        {manualPostPreview.caption_preview.length > 400 ? '…' : ''}
                      </Typography>
                    )}
                  </Alert>
                )}
                {manualPostEntryMode === 'link' &&
                  manualPostLinkMode === 'single' &&
                  manualPostPreview &&
                  manualPostPreview.owner_username_match === false && (
                  <FormControlLabel
                    sx={{ mt: 1, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={manualPostConfirmAssign}
                        onChange={(_, checked) => setManualPostConfirmAssign(checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Bu gönderiyi yine de seçilen influencer ve bu kampanya için kaydetmeyi onaylıyorum
                        {manualPostPreview.owner_username ? (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            (API içerik sahibi: @{manualPostPreview.owner_username})
                          </Typography>
                        ) : null}
                      </Typography>
                    }
                  />
                )}
                {(editingManualPostId || manualPostEntryMode === 'upload' || manualPostLinkMode === 'single') && (
                <>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth>
                      <InputLabel>Caption ifadeleri</InputLabel>
                      <Select
                        multiple
                        label="Caption ifadeleri"
                        value={manualPostKw.selectedCaptionKeywords}
                        onChange={(event) => {
                          const value = event.target.value;
                          setManualPostKw((prev) => ({
                            ...prev,
                            selectedCaptionKeywords: typeof value === 'string' ? value.split(',') : value
                          }));
                        }}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                      >
                        {(campaign?.target_caption_keywords || []).length > 0 ? (
                          (campaign?.target_caption_keywords || []).map((phrase) => {
                            const p = String(phrase || '').trim();
                            if (!p) return null;
                            return (
                              <MenuItem key={p} value={p}>
                                {p}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem disabled>Kriter bulunmuyor</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                  <Button type="submit" variant="contained" color="primary" disabled={manualPostSubmitting} sx={mobileButtonSx}>
                    {editingManualPostId ? 'Güncelle' : 'Kaydet'}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={resetManualPostForm}
                    disabled={manualPostSubmitting}
                    sx={mobileButtonSx}
                  >
                    {editingManualPostId ? 'İptal' : 'Temizle'}
                  </Button>
                </Box>
                </>
                )}
                {!editingManualPostId && manualPostEntryMode === 'link' && manualPostLinkMode === 'bulk' && (
                <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                  <Button type="button" variant="outlined" onClick={resetManualPostForm} disabled={manualPostBulkLoading} sx={mobileButtonSx}>
                    Temizle
                  </Button>
                </Box>
                )}
              </Box>
            )}

            <Divider sx={{ mt: 4, mb: 3 }} />

            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={1} mb={2}>
              <Typography variant="h6">
                Manuel Kayıtlar
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {manualEntries.length} kayıt
              </Typography>
            </Box>

            {manualEntriesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : manualEntries.length ? (
              <TableContainer component={Paper} variant="outlined" sx={responsiveTableContainerSx}>
                <Table size="small" sx={{ minWidth: { xs: 720, sm: 880 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tarih</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Tür</TableCell>
                      <TableCell>Kriterler</TableCell>
                      <TableCell>Medya</TableCell>
                      <TableCell>Ekleyen</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualEntries.map((entry) => (
                      <TableRow key={entry.id} hover selected={editingManualEntryId === entry.id}>
                        <TableCell>
                          {entry.story_timestamp
                            ? new Date(entry.story_timestamp).toLocaleString('tr-TR')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {cleanText(entry.influencer_full_name) || `@${entry.influencer_username}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{entry.influencer_username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getManualStoryTypeLabel(entry.story_type)}
                            color={entry.story_type === 'video' ? 'secondary' : entry.story_type === 'image' ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {entry.detected_keywords.length ? (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {entry.detected_keywords.map((keyword) => (
                                <Chip
                                  key={keyword}
                                  label={keyword}
                                  color={getManualKeywordChipColor(keyword)}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Kriter seçilmedi
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.media_url ? (
                            <Typography variant="body2" color="primary">
                              <a href={entry.media_url} target="_blank" rel="noopener noreferrer">
                                Medyayı Aç
                              </a>
                            </Typography>
                          ) : entry.story_url ? (
                            <Typography variant="body2" color="primary">
                              <a href={entry.story_url} target="_blank" rel="noopener noreferrer">
                                Hikaye Linki
                              </a>
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.added_by_username ? entry.added_by_username : 'Sistem'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.detected_at ? new Date(entry.detected_at).toLocaleString('tr-TR') : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => handleManualEdit(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton size="small" color="error" onClick={() => handleManualDelete(entry.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Bu kampanya için henüz manuel kayıt eklenmemiş. Yukarıdaki formu kullanarak ilk kaydı oluşturabilirsiniz.
              </Alert>
            )}

            <Divider sx={{ mt: 4, mb: 3 }} />

            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={1} mb={2}>
              <Typography variant="h6">
                Manuel Gönderiler / Reels
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {manualTrackedPosts.length} kayıt
              </Typography>
            </Box>

            {manualTrackedPostsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : manualTrackedPosts.length ? (
              <TableContainer component={Paper} variant="outlined" sx={responsiveTableContainerSx}>
                <Table size="small" sx={{ minWidth: { xs: 640, sm: 760 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Önizleme</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Kriterler</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualTrackedPosts.map((post) => {
                      const keywords =
                        typeof post.detected_keywords === 'string'
                          ? (() => {
                              try {
                                return JSON.parse(post.detected_keywords || '[]');
                              } catch {
                                return [];
                              }
                            })()
                          : Array.isArray(post.detected_keywords)
                            ? post.detected_keywords
                            : [];
                      return (
                        <TableRow key={post.id} hover selected={editingManualPostId === post.id}>
                          <TableCell>
                            {post.thumbnail_url ? (
                              <Box
                                component="img"
                                src={post.thumbnail_url}
                                alt=""
                                sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                              />
                            ) : (
                              <Box sx={{ width: 48, height: 48, bgcolor: 'action.hover', borderRadius: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={post.is_reel ? 'Reels' : 'Gönderi'}
                              color={post.is_reel ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              @{post.instagram_username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {keywords.map((keyword: string) => (
                                <Chip
                                  key={keyword}
                                  label={keyword}
                                  color={getManualKeywordChipColor(keyword)}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {post.permalink ? (
                              <Typography variant="body2">
                                <Link href={post.permalink} target="_blank" rel="noopener noreferrer">
                                  Instagram
                                </Link>
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => handleEditManualPost(post)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error" onClick={() => handleDeleteManualPost(post.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Linkten getirerek manuel gönderi eklenmemiş. &quot;Gönderi / Reels&quot; seçip formu kullanın.
              </Alert>
            )}
          </CardContent>
        )}
      </Card>

      <Snackbar
        open={exportFeedback.open}
        autoHideDuration={3000}
        onClose={handleExportFeedbackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleExportFeedbackClose} severity={exportFeedback.severity} sx={{ width: '100%' }}>
          {exportFeedback.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={manualFeedback.open}
        autoHideDuration={3000}
        onClose={handleManualFeedbackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleManualFeedbackClose} severity={manualFeedback.severity} sx={{ width: '100%' }}>
          {manualFeedback.message}
        </Alert>
      </Snackbar>

      <Dialog open={openSettingsDialog} onClose={() => setOpenSettingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Kampanya Takip Ayarları</DialogTitle>
        <DialogContent sx={responsiveDialogContentSx}>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">İçerik takip türü</FormLabel>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Hikâye, gönderi/Reels veya ikisini birlikte izleyin. Hedef hashtag, mention ve linkler her modda aynı listeden kullanılır.
              </Typography>
              <RadioGroup
                row
                value={settings.content_tracking_mode}
                sx={{ gap: { xs: 0, sm: 1 } }}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    content_tracking_mode: e.target.value as ContentTrackingMode
                  })
                }
              >
                <FormControlLabel value="story" control={<Radio />} label="Hikâye" />
                <FormControlLabel value="feed" control={<Radio />} label="Gönderi ve Reels" />
                <FormControlLabel value="both" control={<Radio />} label="İkisi" />
              </RadioGroup>
            </FormControl>

            {settings.content_tracking_mode !== 'story' && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="Son kaç gönderiye bakılsın"
                  value={settings.feed_post_limit}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setSettings({
                      ...settings,
                      feed_post_limit: Number.isNaN(v) ? 12 : Math.min(50, Math.max(1, v))
                    });
                  }}
                  inputProps={{ min: 1, max: 50 }}
                  helperText="API ve otomatik kontrol bu kadar son gönderiyi işler (1–50)."
                  sx={{ maxWidth: { xs: '100%', sm: 280 }, mb: 2 }}
                />
                <Typography variant="subtitle1" gutterBottom>
                  Caption anahtar kelimeleri
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Gönderi metninde geçen ifadeleri arar; hashtag/mention/link hedeflerinden bağımsızdır.
                </Typography>
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  <TextField
                    fullWidth
                    size="small"
                    label="Yeni ifade"
                    value={newCaptionKeyword}
                    onChange={(e) => setNewCaptionKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCaptionKeyword()}
                    placeholder="örnek ürün adı veya kampanya cümlesi"
                  />
                  <Button variant="outlined" onClick={handleAddCaptionKeyword} sx={mobileButtonSx}>
                    Ekle
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {settings.target_caption_keywords.map((phrase, index) => (
                    <Chip
                      key={index}
                      label={phrase}
                      onDelete={() => handleRemoveCaptionKeyword(index)}
                      variant="outlined"
                      color="default"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="subtitle1" gutterBottom>
              Hashtag'ler
            </Typography>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <TextField
                fullWidth
                size="small"
                label="Yeni Hashtag"
                value={newHashtag}
                onChange={(e) => setNewHashtag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddHashtag()}
                placeholder="#örnek"
              />
              <Button variant="outlined" onClick={handleAddHashtag} sx={mobileButtonSx}>
                Ekle
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
              {settings.target_hashtags.map((tag, index) => (
                <Chip
                  key={index}
                  label={`#${tag}`}
                  onDelete={() => handleRemoveHashtag(index)}
                />
              ))}
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Mention'lar
            </Typography>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <TextField
                fullWidth
                size="small"
                label="Yeni Mention"
                value={newMention}
                onChange={(e) => setNewMention(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddMention()}
                placeholder="@kullaniciadi"
              />
              <Button variant="outlined" onClick={handleAddMention} sx={mobileButtonSx}>
                Ekle
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
              {settings.target_mentions.map((mention, index) => (
                <Chip
                  key={index}
                  label={`@${mention}`}
                  onDelete={() => handleRemoveMention(index)}
                />
              ))}
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Link'ler
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Her link için ayrı eşleşme türü belirleyebilirsiniz.<br/>
              <strong>İçeren:</strong> Link URL içinde geçerse eşleşir (esnek)<br/>
              <strong>Tam Eşleşme:</strong> Domain + path tam eşleşmeli (katı)
            </Typography>
            
            {/* Yeni Link Ekleme */}
            <Grid container spacing={1} mb={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Yeni Link"
                  value={newLink}
                  onChange={(e) => setNewLink(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
                  placeholder="https://example.com"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Eşleşme Türü</InputLabel>
                  <Select
                    value={newLinkMatchType}
                    onChange={(e) => setNewLinkMatchType(e.target.value as 'contains' | 'exact')}
                    label="Eşleşme Türü"
                  >
                    <MenuItem value="contains">İçeren</MenuItem>
                    <MenuItem value="exact">Tam Eşleşme</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button 
                  variant="outlined" 
                  onClick={handleAddLink}
                  fullWidth
                  sx={{ height: '40px' }}
                >
                  Ekle
                </Button>
              </Grid>
            </Grid>
            
            {/* Mevcut Linkler - Her biri için dropdown */}
            <Box mb={3}>
              {settings.target_links.map((linkObj, index) => (
                <Box 
                  key={index} 
                  display="flex" 
                  alignItems={{ xs: 'stretch', sm: 'center' }} 
                  gap={1} 
                  mb={1}
                  sx={{ 
                    p: 1, 
                    bgcolor: 'rgba(0,0,0,0.02)', 
                    borderRadius: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      flex: 1, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {linkObj.url}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 140 }, width: { xs: '100%', sm: 'auto' } }}>
                    <Select
                      value={linkObj.matchType}
                      onChange={(e) => handleUpdateLinkMatchType(index, e.target.value as 'contains' | 'exact')}
                      variant="outlined"
                    >
                      <MenuItem value="contains">İçeren</MenuItem>
                      <MenuItem value="exact">Tam Eşleşme</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton 
                    size="small" 
                    onClick={() => handleRemoveLink(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {settings.target_links.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2, textAlign: 'center' }}>
                  Henüz link eklenmemiş
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Otomatik Kontrol Ayarları */}
          <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 1, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              🤖 {autoCheckSettingsCopy.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              {autoCheckSettingsCopy.description}
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.auto_check_enabled}
                  onChange={(e) => setSettings({...settings, auto_check_enabled: e.target.checked})}
                  color="primary"
                />
              }
              label="Otomatik kontrol aktif"
            />
            
            {settings.auto_check_enabled && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Kontrol Aralığı"
                      value={intervalValue}
                      onChange={(e) => setIntervalValue(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1, max: intervalUnit === 'hours' ? 24 : 1440 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Birim</InputLabel>
                      <Select
                        value={intervalUnit}
                        onChange={(e) => setIntervalUnit(e.target.value as 'minutes' | 'hours')}
                        label="Birim"
                      >
                        <MenuItem value="minutes">Dakika</MenuItem>
                        <MenuItem value="hours">Saat</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Başlangıç Saati"
                      value={settings.auto_check_start_time}
                      onChange={(e) => setSettings({ ...settings, auto_check_start_time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Bitiş Saati"
                      value={settings.auto_check_end_time}
                      onChange={(e) => setSettings({ ...settings, auto_check_end_time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                </Grid>
                
                {/* Hazır Ayarlar */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Hızlı Ayarlar:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip 
                      label="30 dk" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(30); setIntervalUnit('minutes'); }}
                      color={intervalValue === 30 && intervalUnit === 'minutes' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="1 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(1); setIntervalUnit('hours'); }}
                      color={intervalValue === 1 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="2 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(2); setIntervalUnit('hours'); }}
                      color={intervalValue === 2 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="4 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(4); setIntervalUnit('hours'); }}
                      color={intervalValue === 4 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="6 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(6); setIntervalUnit('hours'); }}
                      color={intervalValue === 6 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="12 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(12); setIntervalUnit('hours'); }}
                      color={intervalValue === 12 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                    <Chip 
                      label="24 saat" 
                      size="small" 
                      clickable
                      onClick={() => { setIntervalValue(24); setIntervalUnit('hours'); }}
                      color={intervalValue === 24 && intervalUnit === 'hours' ? 'primary' : 'default'}
                    />
                  </Box>
                </Box>
                
                <Typography variant="caption" color="primary" display="block" sx={{ mt: 2, fontWeight: 'bold' }}>
                  ⏰ {intervalUnit === 'hours' ? `${intervalValue} saat = ${intervalValue * 60} dakika` : `${intervalValue} dakika`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Sunucu saati: {serverTime ? serverTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '…'} · Çalışma aralığı: {formatTimeRangeDisplay(settings.auto_check_start_time, settings.auto_check_end_time)}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.check_only_undetected || false}
                      onChange={(e) => setSettings({...settings, check_only_undetected: e.target.checked})}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Sadece tespit edilemeyenleri kontrol et
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {autoCheckSettingsCopy.undetectedHint}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setOpenSettingsDialog(false)}>İptal</Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <AddInfluencerDialog
        open={openInfluencerDialog}
        onClose={() => setOpenInfluencerDialog(false)}
        onSubmit={handleAddInfluencers}
        submitting={addingInfluencers}
        contentTrackingMode={(campaign?.content_tracking_mode || 'story') as ContentTrackingMode}
      />

      <Dialog open={bulkQuotaDialogOpen} onClose={() => setBulkQuotaDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Seçili influencerlara görev kotası</DialogTitle>
        <DialogContent sx={responsiveDialogContentSx}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {selectedInfluencers.length} kişiye aynı hedef uygulanır. 0 = o tür için hedef yok.
          </Typography>
          <TextField
            fullWidth
            label="Hedef hikâye sayısı"
            type="number"
            inputProps={{ min: 0, max: 999 }}
            value={bulkQuotaStories}
            onChange={(e) =>
              setBulkQuotaStories(Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0)))
            }
            sx={{ mb: 2 }}
            disabled={(campaign?.content_tracking_mode || 'story') === 'feed'}
          />
          <TextField
            fullWidth
            label="Hedef gönderi / Reels (toplam)"
            type="number"
            inputProps={{ min: 0, max: 999 }}
            value={bulkQuotaPosts}
            onChange={(e) =>
              setBulkQuotaPosts(Math.min(999, Math.max(0, parseInt(e.target.value, 10) || 0)))
            }
            disabled={(campaign?.content_tracking_mode || 'story') === 'story'}
          />
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setBulkQuotaDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={() => void handleBulkTaskQuotaApply()}>
            Uygula
          </Button>
        </DialogActions>
      </Dialog>

      {/* ZIP İndirme Progress Dialog */}
      <Dialog 
        open={zipDownloadDialog} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DownloadIcon color="primary" />
            <Typography variant="h6">
              {zipModalContext === 'posts' ? 'Gönderiler İndiriliyor' : "Story'ler İndiriliyor"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={responsiveDialogContentSx}>
          <Box sx={{ py: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {zipProgress.percent}%
              </Typography>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={zipProgress.percent} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                mb: 2,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: zipProgress.percent === 100 
                    ? 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)'
                    : 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)'
                }
              }} 
            />
            
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'rgba(33, 150, 243, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(33, 150, 243, 0.2)',
                textAlign: 'center'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                {zipProgress.step}
              </Typography>
            </Box>

            {zipProgress.percent === 100 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                  ZIP dosyası başarıyla indirildi! 🎉
                </Typography>
              </Box>
            )}

            {campaign && (
              <Box mt={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ opacity: 0.7 }}>
                <Typography variant="caption">
                  {zipModalContext === 'posts'
                    ? `Toplam gönderi: ${campaign.recent_posts?.length || 0}`
                    : `Toplam story: ${campaign.recent_stories?.length || 0}`}
                </Typography>
                <Typography variant="caption">
                  Kampanya: {campaign.title}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Rapor Paylaş Dialog */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon color="primary" />
            <Typography variant="h6">Rapor Paylaş</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={responsiveDialogContentSx}>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Bu kampanyanın raporunu harici kullanıcılarla (ekip arkadaşları, marka vs.) paylaşabilirsiniz.
            </Typography>

            <Box sx={{ mt: 3, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={shareEnabled} 
                    onChange={(e) => setShareEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Paylaşımı Aktif Et"
              />
            </Box>

            <TextField
              fullWidth
              label="Özel URL (slug)"
              value={shareSlug}
              onChange={(e) => setShareSlug(e.target.value)}
              helperText={`Örnek: ${window.location.origin}/rapor/${shareSlug || 'kampanya-adi'}`}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch 
                  checked={sharePasswordEnabled} 
                  onChange={(e) => setSharePasswordEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Şifreyi Aktif Et"
            />

            {sharePasswordEnabled && (
              <TextField
                fullWidth
                label="Şifre"
              type="password"
              value={sharePassword}
              onChange={(e) => setSharePassword(e.target.value)}
                sx={{ mb: 2, mt: 1 }}
            />
            )}

            {shareUrl && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Paylaşım Linki:
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
                  <TextField
                    fullWidth
                    value={shareUrl}
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '0.875rem' }
                    }}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyShareLink}
                    sx={mobileButtonSx}
                  >
                    Kopyala
                  </Button>
                </Box>
              </Box>
            )}

            <Alert severity={sharePasswordEnabled ? 'warning' : 'info'} sx={{ mt: 2 }}>
              <Typography variant="caption">
                📊 Paylaşılan raporda: Influencer listesi, kimler paylaştı/paylaşmadı, story sayıları ve tarihler görünür.
                {sharePasswordEnabled ? ' 🔒 Şifre koruması aktif!' : ' 🔓 Şifresiz erişim açık.'}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setShareDialog(false)}>Kapat</Button>
          <Button 
            onClick={handleCreateShareLink} 
            variant="contained" 
            disabled={shareLoading}
            startIcon={shareLoading ? <CircularProgress size={20} /> : <ShareIcon />}
          >
            {shareUrl ? 'Güncelle' : 'Link Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profil Düzenleme Modalı */}
      <Dialog 
        open={editProfileDialog} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Profil Düzenle
          </Box>
        </DialogTitle>
        <DialogContent sx={responsiveDialogContentSx}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instagram Kullanıcı Adı"
                value={editFormData.instagram_username}
                onChange={(e) => setEditFormData({...editFormData, instagram_username: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tam Ad"
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Biyografi"
                value={editFormData.bio}
                onChange={(e) => setEditFormData({...editFormData, bio: e.target.value})}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Takipçi Sayısı"
                type="number"
                value={editFormData.follower_count}
                onChange={(e) => setEditFormData({...editFormData, follower_count: parseInt(e.target.value) || 0})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_private}
                    onChange={(e) => setEditFormData({...editFormData, is_private: e.target.checked})}
                  />
                }
                label="Gizli Hesap"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={handleCancelEdit}>
            İptal
          </Button>
          <Button 
            onClick={handleSaveProfile} 
            variant="contained"
            disabled={!editFormData.instagram_username.trim()}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignDetail;
